import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Typography, Spin, Card, Radio, Table, Checkbox, Select, Button, Tabs, Tag } from 'antd';
import { FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import { ComposedChart, LineChart, AreaChart, BarChart, Bar, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Brush, ReferenceLine, ReferenceDot } from 'recharts';
import GlobalFilterBar from '../components/common/GlobalFilterBar';
import DownloadButton from '../components/common/DownloadButton';
import { useFilters } from '../context/FilterContext';
import { formatCurrencyLakhs, formatChartDateAxis, formatNumberKM, tooltipSorter, aggregateChartData, formatTableData, getTableColumns } from '../utils/formatters';
import { exportCardAsPng, exportTableAsCsv } from '../utils/downloadUtils';
import { getMarketDashboard, getTotalOutageYTD, getPeakDemandMetYTD, getRenewableEnergyYTD, getCoalStockActualVsNormative, getPresentReservoirLevels, getMonthlySolarNonSolarMcp } from '../services/api';
import dayjs from 'dayjs';

const renderCustomLegend = (props) => {
  const { payload } = props;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
      {payload.map((entry, index) => {
        if (entry.value === 'base' || entry.value === 'dummy') return null;
        let color = entry.color;
        if (entry.value === 'Over-Injection (OI)') color = '#ef4444';
        if (entry.value === 'Under-Injection (UI)') color = '#0288D1';
        const isLine = entry.type === 'line';
        return (
          <span key={`legend-${index}`} style={{ display: 'inline-flex', alignItems: 'center', marginRight: 10 }}>
            <span style={{
              display: 'inline-block',
              width: isLine ? 16 : 12,
              height: isLine ? 4 : 12,
              backgroundColor: color,
              marginRight: 6,
              borderRadius: isLine ? 0 : 2
            }} />
            <span style={{ fontSize: 12, color: '#555' }}>{entry.value}</span>
          </span>
        );
      })}
    </div>
  );
};

function KpiCardV2({ title, value, suffix, color }) {
  const fmtVal = value != null ? Number(value).toFixed(2) : '-';
  return (
    <Card size="small" style={{ background: color || '#f0f3ff', borderRadius: 8, height: '100%', padding: '4px 8px' }} bodyStyle={{ padding: 0 }}>
      <Typography.Text type="secondary" style={{ fontSize: 11, fontWeight: 600, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</Typography.Text>
      <div style={{ fontSize: 20, fontWeight: 700, color: '#1B4F72', marginTop: 4, whiteSpace: 'nowrap' }}>
        {fmtVal}{suffix ? <span style={{ fontSize: 13, fontWeight: 400 }}> {suffix}</span> : null}
      </div>
    </Card>
  );
}


// Border and Header ribbon styling for boxed containers and graphs in Market Summary tab.
// Change these constants to update border and header styling across all Market Summary boxes!
const MARKET_SUMMARY_BOX_BORDER = '1px solid #94a3b8';
const MARKET_SUMMARY_CARD_HEAD_STYLE = {
  background: '#f1f5f9',
  borderBottom: '1px solid #cbd5e1',
  padding: '0 20px',
  minHeight: 44,
  borderTopLeftRadius: 7,
  borderTopRightRadius: 7
};

function ThirtyDaysDailyMcpCard({ idPrefix = 'summary' }) {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [selectedSegments, setSelectedSegments] = useState(['GDAM', 'DAM', 'RTM']);
  const [viewMode, setViewMode] = useState('chart');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetch30DaysData = async () => {
      setLoading(true);
      try {
        const today = dayjs();
        const yesterday = today.subtract(1, 'day');
        const thirtyDaysAgo = yesterday.subtract(29, 'day');

        const res = await getMarketDashboard({
          date_from: thirtyDaysAgo.format('YYYY-MM-DD'),
          date_to: yesterday.format('YYYY-MM-DD'),
          granularity: 'daily'
        }).catch(() => null);

        const rows = res?.data?.results || [];

        // Build continuous 30-day array
        const days = [];
        for (let i = 0; i < 30; i++) {
          const cur = thirtyDaysAgo.add(i, 'day');
          const dayStr = cur.format('YYYY-MM-DD');
          const dayLabel = cur.format('DD-MMM');

          const dbMatch = rows.find(r => (r.date === dayStr) || (r.period && r.period.startsWith(dayStr)));

          let gdamVal = dbMatch && dbMatch.gdam_mcp != null ? Number(dbMatch.gdam_mcp) : null;
          let damVal = dbMatch && dbMatch.dam_mcp != null ? Number(dbMatch.dam_mcp) : null;
          let rtmVal = dbMatch && dbMatch.rtm_mcp != null ? Number(dbMatch.rtm_mcp) : null;

          days.push({
            date: dayStr,
            dayLabel,
            gdam_mcp: gdamVal != null ? Number(gdamVal.toFixed(2)) : null,
            dam_mcp: damVal != null ? Number(damVal.toFixed(2)) : null,
            rtm_mcp: rtmVal != null ? Number(rtmVal.toFixed(2)) : null,
          });
        }

        if (isMounted) {
          setChartData(days);
        }
      } catch (err) {
        console.error("Error fetching 30-day market daily data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetch30DaysData();
    return () => { isMounted = false; };
  }, []);

  const cardId = `chart-30d-daily-mcp-${idPrefix}`;

  const allColumns = [
    { title: 'Date', dataIndex: 'dayLabel', key: 'dayLabel', render: (v, r) => <span>{v} ({r.date})</span> },
    { key: 'GDAM', title: 'GDAM MCP (₹/kWh)', dataIndex: 'gdam_mcp', render: v => `₹${Number(v).toFixed(2)}` },
    { key: 'DAM', title: 'DAM MCP (₹/kWh)', dataIndex: 'dam_mcp', render: v => `₹${Number(v).toFixed(2)}` },
    { key: 'RTM', title: 'RTM MCP (₹/kWh)', dataIndex: 'rtm_mcp', render: v => `₹${Number(v).toFixed(2)}` },
  ];

  const tableColumns = allColumns.filter(c => !c.key || c.key === 'dayLabel' || selectedSegments.includes(c.key));

  return (
    <Card
      id={cardId}
      bordered={true}
      headStyle={MARKET_SUMMARY_CARD_HEAD_STYLE}
      style={expanded ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', border: MARKET_SUMMARY_BOX_BORDER, marginBottom: 16 }}
      bodyStyle={{ padding: '20px 24px' }}
      title={
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1B4F72' }}>
            Daily Average MCP (Last 30 Days)
          </div>
        </div>
      }
      extra={
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Checkbox.Group
            options={['GDAM', 'DAM', 'RTM']}
            value={selectedSegments}
            onChange={setSelectedSegments}
          />
          <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)} size="small">
            <Radio.Button value="chart">Chart</Radio.Button>
            <Radio.Button value="table">Table</Radio.Button>
          </Radio.Group>
          {viewMode === 'chart' ? (
            <DownloadButton onPng={() => exportCardAsPng(cardId, `Daily_Avg_MCP_30D_${dayjs().format('DDMMMYYYY')}`)} />
          ) : (
            <DownloadButton onCsv={() => exportTableAsCsv(chartData, tableColumns, `Daily_Avg_MCP_30D_${dayjs().format('DDMMMYYYY')}`)} />
          )}
          <Button icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpanded(!expanded)} />
        </div>
      }
    >
      <Spin spinning={loading}>
        {viewMode === 'chart' ? (
          <div style={{ height: expanded ? 'calc(100vh - 140px)' : 360, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ left: 10, right: 25, bottom: 25, top: 15 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="dayLabel"
                  tick={{ fontSize: 11, fill: '#475569' }}
                  interval={1}
                  angle={-45}
                  textAnchor="end"
                  height={50}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#475569' }}
                  tickFormatter={v => `₹${Math.round(Number(v))}`}
                  domain={['auto', 'auto']}
                  label={{
                    value: 'MCP (₹/kWh)',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontSize: 12, fill: '#64748b' }
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: 'none',
                    zIndex: 9999
                  }}
                  labelFormatter={(label, payload) => {
                    const fullDate = payload?.[0]?.payload?.date;
                    return `Date: ${label}${fullDate ? ` (${fullDate})` : ''}`;
                  }}
                  formatter={(val, name) => [`₹${Number(val).toFixed(2)}/kWh`, name]}
                />
                <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 20 }} />
                <Brush dataKey="dayLabel" height={22} stroke="#888" />
                {selectedSegments.includes('GDAM') && (
                  <Line
                    type="monotone"
                    dataKey="gdam_mcp"
                    name="GDAM MCP"
                    stroke="#006400"
                    strokeWidth={2.2}
                    dot={false}
                    activeDot={{ r: 5 }}
                  />
                )}
                {selectedSegments.includes('DAM') && (
                  <Line
                    type="monotone"
                    dataKey="dam_mcp"
                    name="DAM MCP"
                    stroke="#00008B"
                    strokeWidth={2.2}
                    dot={false}
                    activeDot={{ r: 5 }}
                  />
                )}
                {selectedSegments.includes('RTM') && (
                  <Line
                    type="monotone"
                    dataKey="rtm_mcp"
                    name="RTM MCP"
                    stroke="#8B0000"
                    strokeWidth={2.2}
                    dot={false}
                    activeDot={{ r: 5 }}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <Table
            dataSource={chartData}
            columns={tableColumns}
            size="small"
            scroll={{ x: 'max-content', y: 340 }}
            pagination={false}
            rowKey="date"
          />
        )}
      </Spin>
    </Card>
  );
}

function ThirtyDaysDailyMcvCard({ idPrefix = 'summary' }) {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [selectedSegments, setSelectedSegments] = useState(['GDAM', 'DAM', 'RTM']);
  const [viewMode, setViewMode] = useState('chart');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetch30DaysData = async () => {
      setLoading(true);
      try {
        const today = dayjs();
        const yesterday = today.subtract(1, 'day');
        const thirtyDaysAgo = yesterday.subtract(29, 'day');

        const res = await getMarketDashboard({
          date_from: thirtyDaysAgo.format('YYYY-MM-DD'),
          date_to: yesterday.format('YYYY-MM-DD'),
          granularity: 'daily'
        }).catch(() => null);

        const rows = res?.data?.results || [];

        // Build continuous 30-day array
        const days = [];
        for (let i = 0; i < 30; i++) {
          const cur = thirtyDaysAgo.add(i, 'day');
          const dayStr = cur.format('YYYY-MM-DD');
          const dayLabel = cur.format('DD-MMM');

          const dbMatch = rows.find(r => (r.date === dayStr) || (r.period && r.period.startsWith(dayStr)));

          // Check for MCV in MW: dam_mcv_mw, gdam_mcv_mw, rtm_mcv_mw (fallback to daily mcv * 4000 if stored in MU: 1 MU = 250 MW blocks for 15min, or daily MU / 0.00025 / 96 = MW)
          // dbMatch has gdam_mcv_mw, dam_mcv_mw, rtm_mcv_mw from AVG(market_vol_mw) in SQL
          const getMcvMw = (mwKey, muKey) => {
            if (dbMatch && dbMatch[mwKey] != null && !isNaN(dbMatch[mwKey])) {
              return Number(Number(dbMatch[mwKey]).toFixed(2));
            }
            if (dbMatch && dbMatch[muKey] != null && !isNaN(dbMatch[muKey])) {
              // Daily MU to avg MW: (MU * 1,000,000 kWh) / 24h / 1000 kW = MW (i.e. MU * 1000 / 24)
              // Or in 96 blocks: MU / 0.00025 / 96 = MU * 4000 / 96 = MU * 41.6667 MW
              const mu = Number(dbMatch[muKey]);
              return Number(((mu * 1000) / 24).toFixed(2));
            }
            return null;
          };

          const gdamMcvMw = getMcvMw('gdam_mcv_mw', 'gdam_mcv');
          const damMcvMw = getMcvMw('dam_mcv_mw', 'dam_mcv');
          const rtmMcvMw = getMcvMw('rtm_mcv_mw', 'rtm_mcv');

          days.push({
            date: dayStr,
            dayLabel,
            gdam_mcv_mw: gdamMcvMw,
            dam_mcv_mw: damMcvMw,
            rtm_mcv_mw: rtmMcvMw,
          });
        }

        if (isMounted) {
          setChartData(days);
        }
      } catch (err) {
        console.error("Error fetching 30-day market daily MCV data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetch30DaysData();
    return () => { isMounted = false; };
  }, []);

  const cardId = `chart-30d-daily-mcv-${idPrefix}`;

  const allColumns = [
    { title: 'Date', dataIndex: 'dayLabel', key: 'dayLabel', render: (v, r) => <span>{v} ({r.date})</span> },
    { key: 'GDAM', title: 'GDAM MCV (MW)', dataIndex: 'gdam_mcv_mw', render: v => v != null && !isNaN(v) ? `${Number(v).toFixed(2)} MW` : '-' },
    { key: 'DAM', title: 'DAM MCV (MW)', dataIndex: 'dam_mcv_mw', render: v => v != null && !isNaN(v) ? `${Number(v).toFixed(2)} MW` : '-' },
    { key: 'RTM', title: 'RTM MCV (MW)', dataIndex: 'rtm_mcv_mw', render: v => v != null && !isNaN(v) ? `${Number(v).toFixed(2)} MW` : '-' },
  ];

  const tableColumns = allColumns.filter(c => !c.key || c.key === 'dayLabel' || selectedSegments.includes(c.key));

  return (
    <Card
      id={cardId}
      bordered={true}
      headStyle={MARKET_SUMMARY_CARD_HEAD_STYLE}
      style={expanded ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', border: MARKET_SUMMARY_BOX_BORDER, marginBottom: 16 }}
      bodyStyle={{ padding: '20px 24px' }}
      title={
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1B4F72' }}>
            Daily Average in MCV (Last 30 Days)
          </div>
        </div>
      }
      extra={
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Checkbox.Group
            options={['GDAM', 'DAM', 'RTM']}
            value={selectedSegments}
            onChange={setSelectedSegments}
          />
          <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)} size="small">
            <Radio.Button value="chart">Chart</Radio.Button>
            <Radio.Button value="table">Table</Radio.Button>
          </Radio.Group>
          {viewMode === 'chart' ? (
            <DownloadButton onPng={() => exportCardAsPng(cardId, `Daily_Avg_MCV_30D_${dayjs().format('DDMMMYYYY')}`)} />
          ) : (
            <DownloadButton onCsv={() => exportTableAsCsv(chartData, tableColumns, `Daily_Avg_MCV_30D_${dayjs().format('DDMMMYYYY')}`)} />
          )}
          <Button icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpanded(!expanded)} />
        </div>
      }
    >
      <Spin spinning={loading}>
        {viewMode === 'chart' ? (
          <div style={{ height: expanded ? 'calc(100vh - 140px)' : 360, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ left: 10, right: 25, bottom: 25, top: 15 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="dayLabel"
                  tick={{ fontSize: 11, fill: '#475569' }}
                  interval={1}
                  angle={-45}
                  textAnchor="end"
                  height={50}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#475569' }}
                  tickFormatter={v => `${Number(v).toFixed(0)} MW`}
                  domain={['auto', 'auto']}
                  label={{
                    value: 'MCV (MW)',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontSize: 12, fill: '#64748b' }
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: 'none',
                    zIndex: 9999
                  }}
                  labelFormatter={(label, payload) => {
                    const fullDate = payload?.[0]?.payload?.date;
                    return `Date: ${label}${fullDate ? ` (${fullDate})` : ''}`;
                  }}
                  formatter={(val, name) => [`${Number(val).toFixed(2)} MW`, name]}
                />
                <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 20 }} />
                <Brush dataKey="dayLabel" height={22} stroke="#888" />
                {selectedSegments.includes('GDAM') && (
                  <Line
                    type="monotone"
                    dataKey="gdam_mcv_mw"
                    name="GDAM MCV"
                    stroke="#006400"
                    strokeWidth={2.2}
                    dot={false}
                    activeDot={{ r: 5 }}
                  />
                )}
                {selectedSegments.includes('DAM') && (
                  <Line
                    type="monotone"
                    dataKey="dam_mcv_mw"
                    name="DAM MCV"
                    stroke="#00008B"
                    strokeWidth={2.2}
                    dot={false}
                    activeDot={{ r: 5 }}
                  />
                )}
                {selectedSegments.includes('RTM') && (
                  <Line
                    type="monotone"
                    dataKey="rtm_mcv_mw"
                    name="RTM MCV"
                    stroke="#8B0000"
                    strokeWidth={2.2}
                    dot={false}
                    activeDot={{ r: 5 }}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <Table
            dataSource={chartData}
            columns={tableColumns}
            size="small"
            scroll={{ x: 'max-content', y: 340 }}
            pagination={false}
            rowKey="date"
          />
        )}
      </Spin>
    </Card>
  );
}

function ThirtyDaysBidRatioCard({ idPrefix = 'summary' }) {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [selectedSegments, setSelectedSegments] = useState(['DAM']);
  const [viewMode, setViewMode] = useState('chart');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetch30DaysBidData = async () => {
      setLoading(true);
      try {
        const today = dayjs();
        const yesterday = today.subtract(1, 'day');
        const thirtyDaysAgo = yesterday.subtract(29, 'day');

        const res = await getMarketDashboard({
          date_from: thirtyDaysAgo.format('YYYY-MM-DD'),
          date_to: yesterday.format('YYYY-MM-DD'),
          granularity: 'daily'
        }).catch(() => null);

        const rows = res?.data?.results || [];

        // Build continuous 30-day array
        const days = [];
        for (let i = 0; i < 30; i++) {
          const cur = thirtyDaysAgo.add(i, 'day');
          const dayStr = cur.format('YYYY-MM-DD');
          const dayLabel = cur.format('DD-MMM');

          const dbMatch = rows.find(r => (r.date === dayStr) || (r.period && r.period.startsWith(dayStr)));

          // Extract Purchase and Sell Bids for all 3 segments (summing absolute values)
          let damPur = dbMatch && (dbMatch.dam_purchase_bid_sum != null || dbMatch.dam_purchase_bid != null)
            ? Number(dbMatch.dam_purchase_bid_sum ?? dbMatch.dam_purchase_bid)
            : null;
          let damSell = dbMatch && (dbMatch.dam_sell_bid_sum != null || dbMatch.dam_sell_bid != null)
            ? Number(dbMatch.dam_sell_bid_sum ?? dbMatch.dam_sell_bid)
            : null;

          let gdamPur = dbMatch && (dbMatch.gdam_purchase_bid_sum != null || dbMatch.gdam_purchase_bid != null)
            ? Number(dbMatch.gdam_purchase_bid_sum ?? dbMatch.gdam_purchase_bid)
            : null;
          let gdamSell = dbMatch && (dbMatch.gdam_sell_bid_sum != null || dbMatch.gdam_sell_bid != null)
            ? Number(dbMatch.gdam_sell_bid_sum ?? dbMatch.gdam_sell_bid)
            : null;

          let rtmPur = dbMatch && (dbMatch.rtm_purchase_bid_sum != null || dbMatch.rtm_purchase_bid != null)
            ? Number(dbMatch.rtm_purchase_bid_sum ?? dbMatch.rtm_purchase_bid)
            : null;
          let rtmSell = dbMatch && (dbMatch.rtm_sell_bid_sum != null || dbMatch.rtm_sell_bid != null)
            ? Number(dbMatch.rtm_sell_bid_sum ?? dbMatch.rtm_sell_bid)
            : null;

          const calcRatio = (pur, sell) => {
            if (pur == null || sell == null) return null;
            const absPur = Math.abs(pur);
            const absSell = Math.abs(sell);
            if (absSell > 0) return Number((absPur / absSell).toFixed(3));
            return null;
          };

          const damRatio = calcRatio(damPur, damSell);
          const gdamRatio = calcRatio(gdamPur, gdamSell);
          const rtmRatio = calcRatio(rtmPur, rtmSell);

          days.push({
            date: dayStr,
            dayLabel,
            DAM: damRatio,
            GDAM: gdamRatio,
            RTM: rtmRatio,
            damPur: damPur != null ? Number(damPur.toFixed(1)) : null,
            damSell: damSell != null ? Number(damSell.toFixed(1)) : null,
            gdamPur: gdamPur != null ? Number(gdamPur.toFixed(1)) : null,
            gdamSell: gdamSell != null ? Number(gdamSell.toFixed(1)) : null,
            rtmPur: rtmPur != null ? Number(rtmPur.toFixed(1)) : null,
            rtmSell: rtmSell != null ? Number(rtmSell.toFixed(1)) : null,
          });
        }

        if (isMounted) {
          setChartData(days);
        }
      } catch (err) {
        console.error("Error fetching 30-day bid ratio data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetch30DaysBidData();
    return () => { isMounted = false; };
  }, []);

  const cardId = `chart-30d-bid-ratio-${idPrefix}`;

  const segmentColors = {
    DAM: '#00008B',
    GDAM: '#006400',
    RTM: '#8B0000',
  };

  const tableColumns = [
    { title: 'Date', dataIndex: 'dayLabel', key: 'dayLabel', render: (v, r) => <span>{v} ({r.date})</span> },
    ...(selectedSegments.includes('DAM') ? [
      { title: 'DAM Ratio', dataIndex: 'DAM', key: 'damRatio', render: v => <strong>{Number(v).toFixed(3)}</strong> },
      { title: 'DAM Pur (MW)', dataIndex: 'damPur', key: 'damPur' },
      { title: 'DAM Sell (MW)', dataIndex: 'damSell', key: 'damSell' },
    ] : []),
    ...(selectedSegments.includes('GDAM') ? [
      { title: 'GDAM Ratio', dataIndex: 'GDAM', key: 'gdamRatio', render: v => <strong>{Number(v).toFixed(3)}</strong> },
      { title: 'GDAM Pur (MW)', dataIndex: 'gdamPur', key: 'gdamPur' },
      { title: 'GDAM Sell (MW)', dataIndex: 'gdamSell', key: 'gdamSell' },
    ] : []),
    ...(selectedSegments.includes('RTM') ? [
      { title: 'RTM Ratio', dataIndex: 'RTM', key: 'rtmRatio', render: v => <strong>{Number(v).toFixed(3)}</strong> },
      { title: 'RTM Pur (MW)', dataIndex: 'rtmPur', key: 'rtmPur' },
      { title: 'RTM Sell (MW)', dataIndex: 'rtmSell', key: 'rtmSell' },
    ] : []),
  ];

  return (
    <Card
      id={cardId}
      bordered={true}
      headStyle={MARKET_SUMMARY_CARD_HEAD_STYLE}
      style={expanded ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', border: MARKET_SUMMARY_BOX_BORDER, marginBottom: 16 }}
      bodyStyle={{ padding: '20px 24px' }}
      title={
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1B4F72' }}>
            Buy / Sell Bid Ratio (Last 30 Days)
          </div>
        </div>
      }
      extra={
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Checkbox.Group
            options={['GDAM', 'DAM', 'RTM']}
            value={selectedSegments}
            onChange={setSelectedSegments}
          />
          <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)} size="small">
            <Radio.Button value="chart">Chart</Radio.Button>
            <Radio.Button value="table">Table</Radio.Button>
          </Radio.Group>
          {viewMode === 'chart' ? (
            <DownloadButton onPng={() => exportCardAsPng(cardId, `Bid_Ratio_30D_${dayjs().format('DDMMMYYYY')}`)} />
          ) : (
            <DownloadButton onCsv={() => exportTableAsCsv(chartData, tableColumns, `Bid_Ratio_30D_${dayjs().format('DDMMMYYYY')}`)} />
          )}
          <Button icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpanded(!expanded)} />
        </div>
      }
    >
      <Spin spinning={loading}>
        {viewMode === 'chart' ? (
          <div style={{ height: expanded ? 'calc(100vh - 140px)' : 360, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ left: 10, right: 25, bottom: 25, top: 15 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="dayLabel"
                  tick={{ fontSize: 11, fill: '#475569' }}
                  interval={1}
                  angle={-45}
                  textAnchor="end"
                  height={50}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#475569' }}
                  tickFormatter={v => Math.round(Number(v))}
                  domain={['auto', 'auto']}
                  label={{
                    value: 'Purchase / Sell Ratio',
                    angle: -90,
                    position: 'insideLeft',
                    dy: 40,
                    style: { fontSize: 12, fill: '#64748b' }
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: 'none',
                    zIndex: 9999
                  }}
                  labelFormatter={(label, payload) => {
                    const fullDate = payload?.[0]?.payload?.date;
                    return fullDate || label;
                  }}
                  formatter={(val, name) => [Number(val).toFixed(2), name]}
                />
                <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 20 }} />
                <Brush dataKey="dayLabel" height={22} stroke="#888" />
                {selectedSegments.includes('DAM') && (
                  <Line
                    type="monotone"
                    dataKey="DAM"
                    name="DAM (Purchase/Sell)"
                    stroke={segmentColors.DAM}
                    strokeWidth={2.2}
                    dot={false}
                    activeDot={{ r: 6 }}
                  />
                )}
                {selectedSegments.includes('GDAM') && (
                  <Line
                    type="monotone"
                    dataKey="GDAM"
                    name="GDAM (Purchase/Sell)"
                    stroke={segmentColors.GDAM}
                    strokeWidth={2.2}
                    dot={false}
                    activeDot={{ r: 6 }}
                  />
                )}
                {selectedSegments.includes('RTM') && (
                  <Line
                    type="monotone"
                    dataKey="RTM"
                    name="RTM (Purchase/Sell)"
                    stroke={segmentColors.RTM}
                    strokeWidth={2.2}
                    dot={false}
                    activeDot={{ r: 6 }}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <Table
            dataSource={chartData}
            columns={tableColumns}
            size="small"
            scroll={{ x: 'max-content', y: 340 }}
            pagination={false}
            rowKey="date"
          />
        )}
      </Spin>
    </Card>
  );
}

function ThirtyDaysRealizationVsMarketCard({ idPrefix = 'summary' }) {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [viewMode, setViewMode] = useState('chart');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetch30DaysRealizationData = async () => {
      setLoading(true);
      try {
        const today = dayjs();
        const yesterday = today.subtract(1, 'day');
        const thirtyDaysAgo = yesterday.subtract(29, 'day');

        const res = await getMarketDashboard({
          date_from: thirtyDaysAgo.format('YYYY-MM-DD'),
          date_to: yesterday.format('YYYY-MM-DD'),
          granularity: 'daily'
        }).catch(() => null);

        const rows = res?.data?.results || [];

        // Build continuous 30-day array
        const days = [];
        for (let i = 0; i < 30; i++) {
          const cur = thirtyDaysAgo.add(i, 'day');
          const dayStr = cur.format('YYYY-MM-DD');
          const dayLabel = cur.format('DD-MMM');

          const dbMatch = rows.find(r => (r.date === dayStr) || (r.period && r.period.startsWith(dayStr)));

          // Our Realization (PU - ₹/kWh) -> Exchange Realization
          let ourRealization = dbMatch && dbMatch.gross_tariff_realized_exch != null ? Number(dbMatch.gross_tariff_realized_exch) : null;
          // Market Weighted Average MCP (₹/kWh)
          let marketWtdMcp = dbMatch && (dbMatch.wtd_mcp != null || dbMatch.gross_rev_exch != null)
            ? Number(dbMatch.wtd_mcp ?? dbMatch.gross_rev_exch)
            : null;

          days.push({
            date: dayStr,
            dayLabel,
            our_realization: ourRealization != null ? Number(ourRealization.toFixed(2)) : null,
            market_wtd_mcp: marketWtdMcp != null ? Number(marketWtdMcp.toFixed(2)) : null,
          });
        }

        if (isMounted) {
          setChartData(days);
        }
      } catch (err) {
        console.error("Error fetching 30-day realization vs market data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetch30DaysRealizationData();
    return () => { isMounted = false; };
  }, []);

  const cardId = `chart-30d-realization-vs-market-${idPrefix}`;

  const tableColumns = [
    { title: 'Date', dataIndex: 'dayLabel', key: 'dayLabel', render: (v, r) => <span>{v} ({r.date})</span> },
    { title: 'Our Realization (PU) (₹/kWh)', dataIndex: 'our_realization', key: 'our_realization', render: v => v != null ? `₹${Number(v).toFixed(2)}` : '-' },
    { title: 'Market Weighted Avg MCP (₹/kWh)', dataIndex: 'market_wtd_mcp', key: 'market_wtd_mcp', render: v => v != null ? `₹${Number(v).toFixed(2)}` : '-' },
    {
      title: 'Spread / Delta (₹/kWh)',
      key: 'delta',
      render: (_, r) => {
        if (r.our_realization == null || r.market_wtd_mcp == null) return '-';
        const delta = r.our_realization - r.market_wtd_mcp;
        const color = delta >= 0 ? '#16A085' : '#E74C3C';
        return <span style={{ color, fontWeight: 600 }}>{delta >= 0 ? `+₹${delta.toFixed(2)}` : `-₹${Math.abs(delta).toFixed(2)}`}</span>;
      }
    }
  ];

  return (
    <Card
      id={cardId}
      bordered={true}
      headStyle={MARKET_SUMMARY_CARD_HEAD_STYLE}
      style={expanded ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', border: MARKET_SUMMARY_BOX_BORDER, marginBottom: 16 }}
      bodyStyle={{ padding: '20px 24px' }}
      title={
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1B4F72' }}>
            Our Realization (PU) vs. Market (Weighted Average) (Last 30 Days)
          </div>
        </div>
      }
      extra={
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)} size="small">
            <Radio.Button value="chart">Chart</Radio.Button>
            <Radio.Button value="table">Table</Radio.Button>
          </Radio.Group>
          {viewMode === 'chart' ? (
            <DownloadButton onPng={() => exportCardAsPng(cardId, `Realization_vs_Market_30D_${dayjs().format('DDMMMYYYY')}`)} />
          ) : (
            <DownloadButton onCsv={() => exportTableAsCsv(chartData, tableColumns, `Realization_vs_Market_30D_${dayjs().format('DDMMMYYYY')}`)} />
          )}
          <Button icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpanded(!expanded)} />
        </div>
      }
    >
      <Spin spinning={loading}>
        {viewMode === 'chart' ? (
          <div style={{ height: expanded ? 'calc(100vh - 140px)' : 360, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ left: 10, right: 25, bottom: 25, top: 15 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="dayLabel"
                  tick={{ fontSize: 11, fill: '#475569' }}
                  interval={1}
                  angle={-45}
                  textAnchor="end"
                  height={50}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#475569' }}
                  tickFormatter={v => `₹${Math.round(Number(v))}`}
                  domain={['auto', 'auto']}
                  label={{
                    value: 'Rate (₹/kWh)',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontSize: 12, fill: '#64748b' }
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: 'none',
                    zIndex: 9999
                  }}
                  labelFormatter={(label, payload) => {
                    const fullDate = payload?.[0]?.payload?.date;
                    return fullDate || label;
                  }}
                  formatter={(val, name) => [val != null ? `₹${Number(val).toFixed(2)}/kWh` : '-', name]}
                />
                <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 20 }} />
                <Brush dataKey="dayLabel" height={22} stroke="#888" />
                <Line
                  type="monotone"
                  dataKey="our_realization"
                  name="Our Realization (PU)"
                  stroke="#16A085"
                  strokeWidth={2.4}
                  dot={false}
                  activeDot={{ r: 6 }}
                />
                <Line
                  type="monotone"
                  dataKey="market_wtd_mcp"
                  name="Market (Weighted Avg)"
                  stroke="#8E44AD"
                  strokeWidth={2.4}
                  dot={false}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <Table
            dataSource={chartData}
            columns={tableColumns}
            size="small"
            scroll={{ x: 'max-content', y: 340 }}
            pagination={false}
            rowKey="date"
          />
        )}
      </Spin>
    </Card>
  );
}

function TotalOutageYtdCard({ idPrefix = 'summary' }) {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [rangeType, setRangeType] = useState('year'); // '30d' or 'year'
  const [viewMode, setViewMode] = useState('chart');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchTotalOutageData = async () => {
      setLoading(true);
      try {
        const res = await getTotalOutageYTD({ range: rangeType }).catch(() => null);
        const rows = res?.data?.results || [];
        if (isMounted) {
          setChartData(rows);
        }
      } catch (err) {
        console.error("Error fetching total outage data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchTotalOutageData();
    return () => { isMounted = false; };
  }, [rangeType]);

  const cardId = `chart-total-outage-${rangeType}-${idPrefix}`;

  const tableColumns = [
    {
      title: rangeType === '30d' ? 'Date' : 'Month',
      dataIndex: 'label',
      key: 'label',
      render: (v, r) => <span style={{ textTransform: 'lowercase', fontWeight: 600 }}>{rangeType === '30d' && r?.date ? dayjs(r.date).format('DD-MM-YYYY') : v}</span>
    },
    { title: 'State Sector Outage Avg (GW)', dataIndex: 'outage_state_avg', key: 'outage_state_avg', align: 'right', render: v => v != null ? `${Number(v).toFixed(2)} GW` : '-' },
    { title: 'Central Sector Outage Avg (GW)', dataIndex: 'outage_central_avg', key: 'outage_central_avg', align: 'right', render: v => v != null ? `${Number(v).toFixed(2)} GW` : '-' },
    { title: 'Total Outage (GW)', dataIndex: 'total_outage_mw', key: 'total_outage_mw', align: 'right', render: v => v != null ? <span style={{ fontWeight: 700, color: '#D97706' }}>{Number(v).toFixed(2)} GW</span> : '-' },
  ];

  return (
    <Card
      id={cardId}
      bordered={true}
      headStyle={MARKET_SUMMARY_CARD_HEAD_STYLE}
      style={expanded ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', border: MARKET_SUMMARY_BOX_BORDER, marginBottom: 20 }}
      bodyStyle={{ padding: '20px 24px' }}
      title={
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1B4F72' }}>
            Total Outage ({rangeType === '30d' ? 'Last 30 Days' : 'Last Year'})
          </div>
        </div>
      }
      extra={
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Radio.Group value={rangeType} onChange={e => setRangeType(e.target.value)} size="small" buttonStyle="solid">
            <Radio.Button value="30d">Last 30 Days</Radio.Button>
            <Radio.Button value="year">Last Year</Radio.Button>
          </Radio.Group>
          <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)} size="small">
            <Radio.Button value="chart">Chart</Radio.Button>
            <Radio.Button value="table">Table</Radio.Button>
          </Radio.Group>
          {viewMode === 'chart' ? (
            <DownloadButton onPng={() => exportCardAsPng(cardId, `Total_Outage_${rangeType.toUpperCase()}_${dayjs().format('MMMYYYY')}`)} />
          ) : (
            <DownloadButton onCsv={() => exportTableAsCsv(chartData, tableColumns, `Total_Outage_${rangeType.toUpperCase()}_${dayjs().format('MMMYYYY')}`)} />
          )}
          <Button icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpanded(!expanded)} />
        </div>
      }
    >
      <Spin spinning={loading}>
        {viewMode === 'chart' ? (
          <div style={{ height: expanded ? 'calc(100vh - 140px)' : 380, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ left: 15, right: 25, bottom: rangeType === '30d' ? 35 : 25, top: 15 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={rangeType === '30d' ? { fontSize: 10, fill: '#475569', angle: -45, textAnchor: 'end', dy: 10 } : { fontSize: 11, fill: '#475569' }}
                  height={rangeType === '30d' ? 55 : 30}
                  interval={rangeType === '30d' ? 'preserveStartEnd' : 0}
                  tickFormatter={v => (v ? v.toLowerCase() : '')}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#475569' }}
                  tickFormatter={v => `${Number(v).toFixed(1)}`}
                  domain={['auto', 'auto']}
                  label={{
                    value: 'Total Outage (GW)',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontSize: 12, fill: '#64748b' },
                    dy: 60,
                    dx: -12
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: 'none',
                    zIndex: 9999
                  }}
                  labelFormatter={(label, items) => {
                    const row = items?.[0]?.payload;
                    if (rangeType === '30d' && row?.date) {
                      return `Date: ${dayjs(row.date).format('DD-MM-YYYY')}`;
                    }
                    return `Month: ${label ? label.toLowerCase() : ''}`;
                  }}
                  formatter={(val, name) => [
                    val != null ? `${Number(val).toFixed(2)} GW` : '-',
                    name
                  ]}
                />
                <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 20 }} />
                <Line
                  type="monotone"
                  dataKey="total_outage_mw"
                  name="Total Outage (GW)"
                  stroke="#D97706"
                  strokeWidth={2.5}
                  dot={rangeType === '30d' ? { r: 3, fill: '#D97706' } : { r: 4, fill: '#D97706' }}
                  activeDot={{ r: 7 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <Table
            dataSource={chartData}
            columns={tableColumns}
            size="small"
            scroll={{ x: 'max-content', y: 340 }}
            pagination={false}
            rowKey={rangeType === '30d' ? 'date' : 'label'}
          />
        )}
      </Spin>
    </Card>
  );
}

function PeakDemandMetYtdCard({ idPrefix = 'summary' }) {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [rangeType, setRangeType] = useState('year'); // '30d' or 'year'
  const [viewMode, setViewMode] = useState('chart');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchPeakDemandData = async () => {
      setLoading(true);
      try {
        const res = await getPeakDemandMetYTD({ range: rangeType }).catch(() => null);
        const rows = res?.data?.results || [];
        if (isMounted) {
          setChartData(rows);
        }
      } catch (err) {
        console.error("Error fetching peak demand met data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchPeakDemandData();
    return () => { isMounted = false; };
  }, [rangeType]);

  const cardId = `chart-peak-demand-met-${rangeType}-${idPrefix}`;

  const tableColumns = [
    {
      title: rangeType === '30d' ? 'Date' : 'Month',
      dataIndex: 'label',
      key: 'label',
      render: (v, r) => <span style={{ textTransform: 'lowercase', fontWeight: 600 }}>{rangeType === '30d' && r?.date ? dayjs(r.date).format('DD-MM-YYYY') : v}</span>
    },
    { title: 'Solar Hours Avg (GW)', dataIndex: 'solar_hr_avg_mw', key: 'solar_hr_avg_mw', align: 'right', render: v => v != null ? `${Number(v).toFixed(2)} GW` : '-' },
    { title: 'Non-Solar Hours Avg (GW)', dataIndex: 'non_solar_hr_avg_mw', key: 'non_solar_hr_avg_mw', align: 'right', render: v => v != null ? `${Number(v).toFixed(2)} GW` : '-' },
    { title: 'Peak Demand Met (GW)', dataIndex: 'peak_demand_met_mw', key: 'peak_demand_met_mw', align: 'right', render: v => v != null ? <span style={{ fontWeight: 700, color: '#1E40AF' }}>{Number(v).toFixed(2)} GW</span> : '-' },
  ];

  return (
    <Card
      id={cardId}
      bordered={true}
      headStyle={MARKET_SUMMARY_CARD_HEAD_STYLE}
      style={expanded ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', border: MARKET_SUMMARY_BOX_BORDER, marginBottom: 20 }}
      bodyStyle={{ padding: '20px 24px' }}
      title={
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1B4F72' }}>
            Peak Demand Met ({rangeType === '30d' ? 'Last 30 Days' : 'Last Year'})
          </div>
        </div>
      }
      extra={
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Radio.Group value={rangeType} onChange={e => setRangeType(e.target.value)} size="small" buttonStyle="solid">
            <Radio.Button value="30d">Last 30 Days</Radio.Button>
            <Radio.Button value="year">Last Year</Radio.Button>
          </Radio.Group>
          <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)} size="small">
            <Radio.Button value="chart">Chart</Radio.Button>
            <Radio.Button value="table">Table</Radio.Button>
          </Radio.Group>
          {viewMode === 'chart' ? (
            <DownloadButton onPng={() => exportCardAsPng(cardId, `Peak_Demand_Met_${rangeType.toUpperCase()}_${dayjs().format('MMMYYYY')}`)} />
          ) : (
            <DownloadButton onCsv={() => exportTableAsCsv(chartData, tableColumns, `Peak_Demand_Met_${rangeType.toUpperCase()}_${dayjs().format('MMMYYYY')}`)} />
          )}
          <Button icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpanded(!expanded)} />
        </div>
      }
    >
      <Spin spinning={loading}>
        {viewMode === 'chart' ? (
          <div style={{ height: expanded ? 'calc(100vh - 140px)' : 380, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ left: 15, right: 25, bottom: rangeType === '30d' ? 35 : 25, top: 15 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={rangeType === '30d' ? { fontSize: 10, fill: '#475569', angle: -45, textAnchor: 'end', dy: 10 } : { fontSize: 11, fill: '#475569' }}
                  height={rangeType === '30d' ? 55 : 30}
                  interval={rangeType === '30d' ? 'preserveStartEnd' : 0}
                  tickFormatter={v => (v ? v.toLowerCase() : '')}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#475569' }}
                  tickFormatter={v => `${Number(v).toFixed(1)}`}
                  domain={['auto', 'auto']}
                  label={{
                    value: 'Peak Demand Met (GW)',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontSize: 12, fill: '#64748b' },
                    dy: 70,
                    dx: -12
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: 'none',
                    zIndex: 9999
                  }}
                  labelFormatter={(label, items) => {
                    const row = items?.[0]?.payload;
                    if (rangeType === '30d' && row?.date) {
                      return `Date: ${dayjs(row.date).format('DD-MM-YYYY')}`;
                    }
                    return `Month: ${label ? label.toLowerCase() : ''}`;
                  }}
                  formatter={(val, name) => [
                    val != null ? `${Number(val).toFixed(2)} GW` : '-',
                    name
                  ]}
                />
                <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 20 }} />
                <Line
                  type="monotone"
                  dataKey="peak_demand_met_mw"
                  name="Peak Demand Met (GW)"
                  stroke="#1E40AF"
                  strokeWidth={2.5}
                  dot={rangeType === '30d' ? { r: 3, fill: '#1E40AF' } : { r: 4, fill: '#1E40AF' }}
                  activeDot={{ r: 7 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <Table
            dataSource={chartData}
            columns={tableColumns}
            size="small"
            scroll={{ x: 'max-content', y: 340 }}
            pagination={false}
            rowKey={rangeType === '30d' ? 'date' : 'label'}
          />
        )}
      </Spin>
    </Card>
  );
}

function RenewableEnergyYtdCard({ idPrefix = 'summary' }) {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [rangeType, setRangeType] = useState('year'); // '30d' or 'year'
  const [selectedConstituents, setSelectedConstituents] = useState([]);
  const [viewMode, setViewMode] = useState('chart');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchREData = async () => {
      setLoading(true);
      try {
        const res = await getRenewableEnergyYTD({ range: rangeType }).catch(() => null);
        const rows = res?.data?.results || [];
        if (isMounted) {
          setChartData(rows);
        }
      } catch (err) {
        console.error("Error fetching renewable energy data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchREData();
    return () => { isMounted = false; };
  }, [rangeType]);

  const cardId = `chart-renewable-energy-${rangeType}-${idPrefix}`;

  const allColumns = [
    {
      title: rangeType === '30d' ? 'Date' : 'Month',
      dataIndex: 'label',
      key: 'label',
      render: (v, r) => <span style={{ textTransform: 'lowercase', fontWeight: 600 }}>{rangeType === '30d' && r?.date ? dayjs(r.date).format('DD-MM-YYYY') : v}</span>
    },
    { key: 'Solar', title: 'Solar (MU)', dataIndex: 'total_solar_gen_mu', align: 'right', render: v => v != null ? `${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 3 })} MU` : '-' },
    { key: 'Wind', title: 'Wind (MU)', dataIndex: 'total_wind_gen_mu', align: 'right', render: v => v != null ? `${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 3 })} MU` : '-' },
    { key: 'Hydro', title: 'Hydro (MU)', dataIndex: 'total_hydro_gen_mu', align: 'right', render: v => v != null ? `${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 3 })} MU` : '-' },
    { key: 'total_re', title: 'Total RE (MU)', dataIndex: 'total_re_mu', align: 'right', render: v => v != null ? <span style={{ fontWeight: 700, color: '#16A34A' }}>{Number(v).toLocaleString('en-IN', { maximumFractionDigits: 3 })} MU</span> : '-' },
  ];

  const tableColumns = allColumns.filter(c => !c.key || c.key === 'label' || c.key === 'total_re' || selectedConstituents.includes(c.key));

  return (
    <Card
      id={cardId}
      bordered={true}
      headStyle={MARKET_SUMMARY_CARD_HEAD_STYLE}
      style={expanded ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', border: MARKET_SUMMARY_BOX_BORDER, marginBottom: 20 }}
      bodyStyle={{ padding: '20px 24px' }}
      title={
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1B4F72' }}>
            Renewable Energy ({rangeType === '30d' ? 'Last 30 Days' : 'Last Year'})
          </div>
        </div>
      }
      extra={
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Radio.Group value={rangeType} onChange={e => setRangeType(e.target.value)} size="small" buttonStyle="solid">
            <Radio.Button value="30d">Last 30 Days</Radio.Button>
            <Radio.Button value="year">Last Year</Radio.Button>
          </Radio.Group>
          <Checkbox.Group
            options={['Solar', 'Wind', 'Hydro']}
            value={selectedConstituents}
            onChange={setSelectedConstituents}
          />
          <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)} size="small">
            <Radio.Button value="chart">Chart</Radio.Button>
            <Radio.Button value="table">Table</Radio.Button>
          </Radio.Group>
          {viewMode === 'chart' ? (
            <DownloadButton onPng={() => exportCardAsPng(cardId, `Renewable_Energy_${rangeType.toUpperCase()}_${dayjs().format('MMMYYYY')}`)} />
          ) : (
            <DownloadButton onCsv={() => exportTableAsCsv(chartData, tableColumns, `Renewable_Energy_${rangeType.toUpperCase()}_${dayjs().format('MMMYYYY')}`)} />
          )}
          <Button icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpanded(!expanded)} />
        </div>
      }
    >
      <Spin spinning={loading}>
        {viewMode === 'chart' ? (
          <div style={{ height: expanded ? 'calc(100vh - 140px)' : 380, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ left: 15, right: 25, bottom: rangeType === '30d' ? 35 : 25, top: 15 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={rangeType === '30d' ? { fontSize: 10, fill: '#475569', angle: -45, textAnchor: 'end', dy: 10 } : { fontSize: 11, fill: '#475569' }}
                  height={rangeType === '30d' ? 55 : 30}
                  interval={rangeType === '30d' ? 'preserveStartEnd' : 0}
                  tickFormatter={v => (v ? v.toLowerCase() : '')}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#475569' }}
                  tickFormatter={v => `${Math.round(Number(v)).toLocaleString('en-IN')}`}
                  domain={['auto', 'auto']}
                  label={{
                    value: 'Generation (MU)',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontSize: 12, fill: '#64748b' },
                    dy: 60,
                    dx: -12
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: 'none',
                    zIndex: 9999
                  }}
                  labelFormatter={(label, items) => {
                    const row = items?.[0]?.payload;
                    if (rangeType === '30d' && row?.date) {
                      return `Date: ${dayjs(row.date).format('DD-MM-YYYY')}`;
                    }
                    return `Month: ${label ? label.toLowerCase() : ''}`;
                  }}
                  formatter={(val, name) => [
                    val != null ? `${Number(val).toLocaleString('en-IN', { maximumFractionDigits: 3 })} MU` : '-',
                    name
                  ]}
                />
                <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 20 }} />
                <Line
                  type="monotone"
                  dataKey="total_re_mu"
                  name="Total RE (MU)"
                  stroke="#16A34A"
                  strokeWidth={2.5}
                  dot={rangeType === '30d' ? { r: 3, fill: '#16A34A' } : { r: 4, fill: '#16A34A' }}
                  activeDot={{ r: 7 }}
                />
                {selectedConstituents.includes('Solar') && (
                  <Line
                    type="monotone"
                    dataKey="total_solar_gen_mu"
                    name="Solar (MU)"
                    stroke="#F59E0B"
                    strokeWidth={2}
                    strokeDasharray="4 2"
                    dot={{ r: 3, fill: '#F59E0B' }}
                    activeDot={{ r: 6 }}
                  />
                )}
                {selectedConstituents.includes('Wind') && (
                  <Line
                    type="monotone"
                    dataKey="total_wind_gen_mu"
                    name="Wind (MU)"
                    stroke="#0284C7"
                    strokeWidth={2}
                    strokeDasharray="4 2"
                    dot={{ r: 3, fill: '#0284C7' }}
                    activeDot={{ r: 6 }}
                  />
                )}
                {selectedConstituents.includes('Hydro') && (
                  <Line
                    type="monotone"
                    dataKey="total_hydro_gen_mu"
                    name="Hydro (MU)"
                    stroke="#7C3AED"
                    strokeWidth={2}
                    strokeDasharray="4 2"
                    dot={{ r: 3, fill: '#7C3AED' }}
                    activeDot={{ r: 6 }}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <Table
            dataSource={chartData}
            columns={tableColumns}
            size="small"
            scroll={{ x: 'max-content', y: 340 }}
            pagination={false}
            rowKey={rangeType === '30d' ? 'date' : 'label'}
          />
        )}
      </Spin>
    </Card>
  );
}

function CoalStockActualVsNormativeCard({ idPrefix = 'summary' }) {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [rangeType, setRangeType] = useState('year'); // '30d' or 'year'
  const [viewMode, setViewMode] = useState('chart');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchCoalStockData = async () => {
      setLoading(true);
      try {
        const res = await getCoalStockActualVsNormative({ range: rangeType }).catch(() => null);
        let rows = res?.data?.results || [];
        if (rangeType === '30d') {
          // Ensure continuous 30-day sequence ending at today - 1
          const rowMap = new Map();
          rows.forEach(r => {
            if (r.date) rowMap.set(r.date, r);
          });
          const filled = [];
          const endDay = dayjs().subtract(1, 'day');
          for (let i = 29; i >= 0; i--) {
            const targetDate = endDay.subtract(i, 'day');
            const dateStr = targetDate.format('YYYY-MM-DD');
            if (rowMap.has(dateStr)) {
              filled.push(rowMap.get(dateStr));
            } else {
              filled.push({
                date: dateStr,
                label: targetDate.format('DD-MMM').toLowerCase(),
                pct_actual_vs_normative_stock: null,
              });
            }
          }
          rows = filled;
        }
        if (isMounted) {
          setChartData(rows);
        }
      } catch (err) {
        console.error("Error fetching coal stock data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchCoalStockData();
    return () => { isMounted = false; };
  }, [rangeType]);

  const cardId = `chart-coal-stock-${rangeType}-${idPrefix}`;

  const tableColumns = [
    {
      title: rangeType === '30d' ? 'Date' : 'Month',
      dataIndex: 'label',
      key: 'label',
      render: (v, r) => <span style={{ fontWeight: 600 }}>{rangeType === '30d' && r?.date ? dayjs(r.date).format('DD-MM-YYYY') : v}</span>
    },
    { title: '% Actual vs Normative Stock', dataIndex: 'pct_actual_vs_normative_stock', key: 'pct_actual_vs_normative_stock', align: 'right', render: v => v != null ? <span style={{ fontWeight: 700, color: '#D97706' }}>{Number(v).toFixed(2)}%</span> : '-' },
  ];

  return (
    <Card
      id={cardId}
      bordered={true}
      headStyle={MARKET_SUMMARY_CARD_HEAD_STYLE}
      style={expanded ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', border: MARKET_SUMMARY_BOX_BORDER, marginBottom: 20 }}
      bodyStyle={{ padding: '20px 24px' }}
      title={
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1B4F72' }}>
            % of Actual vs Normative Stock (Coal) ({rangeType === '30d' ? 'Last 30 Days' : 'Last Year'})
          </div>
        </div>
      }
      extra={
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Radio.Group value={rangeType} onChange={e => setRangeType(e.target.value)} size="small" buttonStyle="solid">
            <Radio.Button value="30d">Last 30 Days</Radio.Button>
            <Radio.Button value="year">Last Year</Radio.Button>
          </Radio.Group>
          <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)} size="small">
            <Radio.Button value="chart">Chart</Radio.Button>
            <Radio.Button value="table">Table</Radio.Button>
          </Radio.Group>
          {viewMode === 'chart' ? (
            <DownloadButton onPng={() => exportCardAsPng(cardId, `Coal_Stock_Actual_vs_Normative_${rangeType.toUpperCase()}_${dayjs().format('MMMYYYY')}`)} />
          ) : (
            <DownloadButton onCsv={() => exportTableAsCsv(chartData, tableColumns, `Coal_Stock_Actual_vs_Normative_${rangeType.toUpperCase()}_${dayjs().format('MMMYYYY')}`)} />
          )}
          <Button icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpanded(!expanded)} />
        </div>
      }
    >
      <Spin spinning={loading}>
        {viewMode === 'chart' ? (
          <div style={{ height: expanded ? 'calc(100vh - 140px)' : 380, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ left: 15, right: 25, bottom: rangeType === '30d' ? 35 : 25, top: 15 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: '#475569', angle: -45, textAnchor: 'end', dy: 10 }}
                  height={55}
                  interval={rangeType === '30d' ? 'preserveStartEnd' : 0}
                  tickFormatter={v => (v ? v.toLowerCase() : '')}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#475569' }}
                  tickFormatter={v => `${Math.round(Number(v))}%`}
                  domain={['auto', 'auto']}
                  label={{
                    value: '% Stock',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontSize: 12, fill: '#64748b' },
                    dy: 40,
                    dx: -12
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: 'none',
                    zIndex: 9999
                  }}
                  labelFormatter={(label, items) => {
                    const row = items?.[0]?.payload;
                    if (rangeType === '30d' && row?.date) {
                      return `Date: ${dayjs(row.date).format('DD-MM-YYYY')}`;
                    }
                    return `Month: ${label ? label.toLowerCase() : ''}`;
                  }}
                  formatter={(val, name) => [
                    val != null ? `${Number(val).toFixed(2)}%` : '-',
                    name
                  ]}
                />
                <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 20 }} />
                <Line
                  type="monotone"
                  dataKey="pct_actual_vs_normative_stock"
                  name="% Actual vs Normative Stock"
                  stroke="#D97706"
                  strokeWidth={2.2}
                  dot={{ r: 3, fill: '#D97706' }}
                  activeDot={{ r: 6 }}
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <Table
            dataSource={chartData}
            columns={tableColumns}
            size="small"
            scroll={{ x: 'max-content', y: 340 }}
            pagination={false}
            rowKey={rangeType === '30d' ? 'date' : 'label'}
          />
        )}
      </Spin>
    </Card>
  );
}

function PresentReservoirLevelsCard({ idPrefix = 'summary' }) {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [rangeType, setRangeType] = useState('year'); // '30d' or 'year'
  const [viewMode, setViewMode] = useState('chart');
  const [expanded, setExpanded] = useState(false);
  const [minLevel, setMinLevel] = useState(null);
  const [maxLevel, setMaxLevel] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchReservoirData = async () => {
      setLoading(true);
      try {
        const res = await getPresentReservoirLevels({ range: rangeType }).catch(() => null);
        let rows = res?.data?.results || [];
        if (rangeType === '30d') {
          // Ensure continuous 30-day sequence ending at today - 1
          const rowMap = new Map();
          rows.forEach(r => {
            if (r.date) rowMap.set(r.date, r);
          });
          const filled = [];
          const endDay = dayjs().subtract(1, 'day');
          for (let i = 29; i >= 0; i--) {
            const targetDate = endDay.subtract(i, 'day');
            const dateStr = targetDate.format('YYYY-MM-DD');
            if (rowMap.has(dateStr)) {
              filled.push(rowMap.get(dateStr));
            } else {
              filled.push({
                date: dateStr,
                label: targetDate.format('DD-MMM').toLowerCase(),
                avg_present_reservoir_level_m: null,
              });
            }
          }
          rows = filled;
        }
        if (isMounted) {
          setChartData(rows);
          const validLevels = rows.map(r => r.avg_present_reservoir_level_m).filter(v => v != null);
          const computedMin = validLevels.length > 0 ? Math.min(...validLevels) : null;
          const computedMax = validLevels.length > 0 ? Math.max(...validLevels) : null;
          setMinLevel(res?.data?.min_reservoir_level ?? computedMin);
          setMaxLevel(res?.data?.max_reservoir_level ?? computedMax);
        }
      } catch (err) {
        console.error("Error fetching reservoir levels data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchReservoirData();
    return () => { isMounted = false; };
  }, [rangeType]);

  const cardId = `chart-reservoir-levels-${rangeType}-${idPrefix}`;

  const tableColumns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (v) => <span style={{ fontWeight: 600 }}>{v ? dayjs(v).format('DD-MM-YYYY') : '-'}</span>
    },
    {
      title: 'Avg Present Reservoir Level (m)',
      dataIndex: 'avg_present_reservoir_level_m',
      key: 'avg_present_reservoir_level_m',
      align: 'right',
      render: v => v != null ? <span style={{ fontWeight: 700, color: '#0284C7' }}>{Number(v).toFixed(3)} m</span> : '-'
    },
  ];

  return (
    <Card
      id={cardId}
      bordered={true}
      headStyle={MARKET_SUMMARY_CARD_HEAD_STYLE}
      style={expanded ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', border: MARKET_SUMMARY_BOX_BORDER, marginBottom: 20 }}
      bodyStyle={{ padding: '20px 24px' }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1B4F72' }}>
            Present Reservoir levels ({rangeType === '30d' ? 'Last 30 Days' : 'Last Year'})
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{
              fontSize: 12,
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: 6,
              background: '#EFF6FF',
              color: '#1D4ED8',
              border: '1px solid #BFDBFE'
            }}>
              Max: {maxLevel != null ? `${Number(maxLevel).toFixed(2)} m` : '-'}
            </span>
            <span style={{
              fontSize: 12,
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: 6,
              background: '#F0FDF4',
              color: '#15803D',
              border: '1px solid #BBF7D0'
            }}>
              Min: {minLevel != null ? `${Number(minLevel).toFixed(2)} m` : '-'}
            </span>
          </div>
        </div>
      }
      extra={
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Radio.Group value={rangeType} onChange={e => setRangeType(e.target.value)} size="small" buttonStyle="solid">
            <Radio.Button value="30d">Last 30 Days</Radio.Button>
            <Radio.Button value="year">Last Year</Radio.Button>
          </Radio.Group>
          <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)} size="small">
            <Radio.Button value="chart">Chart</Radio.Button>
            <Radio.Button value="table">Table</Radio.Button>
          </Radio.Group>
          {viewMode === 'chart' ? (
            <DownloadButton onPng={() => exportCardAsPng(cardId, `Present_Reservoir_Levels_${rangeType.toUpperCase()}_${dayjs().format('MMMYYYY')}`)} />
          ) : (
            <DownloadButton onCsv={() => exportTableAsCsv(chartData, tableColumns, `Present_Reservoir_Levels_${rangeType.toUpperCase()}_${dayjs().format('MMMYYYY')}`)} />
          )}
          <Button icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpanded(!expanded)} />
        </div>
      }
    >
      <Spin spinning={loading}>
        {viewMode === 'chart' ? (
          <div style={{ height: expanded ? 'calc(100vh - 140px)' : 380, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ left: 15, right: 25, bottom: 35, top: 15 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: '#475569', angle: -45, textAnchor: 'end', dy: 10 }}
                  height={55}
                  interval={rangeType === '30d' ? 'preserveStartEnd' : (chartData.length > 60 ? Math.floor(chartData.length / 20) : 'preserveStartEnd')}
                  tickFormatter={v => (v ? v.toLowerCase() : '')}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#475569' }}
                  tickFormatter={v => `${Math.round(Number(v))} m`}
                  domain={['auto', 'auto']}
                  label={{
                    value: 'Reservoir Level (m)',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontSize: 12, fill: '#64748b' },
                    dy: 55,
                    dx: -12
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: 'none',
                    zIndex: 9999
                  }}
                  labelFormatter={(label, items) => {
                    const row = items?.[0]?.payload;
                    if (row?.date) {
                      return `Date: ${dayjs(row.date).format('DD-MM-YYYY')}`;
                    }
                    return `Date: ${label ? label.toLowerCase() : ''}`;
                  }}
                  formatter={(val, name) => [
                    val != null ? `${Number(val).toFixed(3)} m` : '-',
                    name
                  ]}
                />
                <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 20 }} />
                <Brush dataKey="label" height={22} stroke="#888" />
                <Line
                  type="monotone"
                  dataKey="avg_present_reservoir_level_m"
                  name="Avg Present Reservoir Level (m)"
                  stroke="#0284C7"
                  strokeWidth={2.2}
                  dot={{ r: 2.5, fill: '#0284C7' }}
                  activeDot={{ r: 6 }}
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <Table
            dataSource={chartData}
            columns={tableColumns}
            size="small"
            scroll={{ x: 'max-content', y: 340 }}
            pagination={false}
            rowKey="date"
          />
        )}
      </Spin>
    </Card>
  );
}

const TIME_OPTIONS = [
  { value: '00:00', label: '12:00 AM' },
  { value: '01:00', label: '01:00 AM' },
  { value: '02:00', label: '02:00 AM' },
  { value: '03:00', label: '03:00 AM' },
  { value: '04:00', label: '04:00 AM' },
  { value: '05:00', label: '05:00 AM' },
  { value: '06:00', label: '06:00 AM' },
  { value: '07:00', label: '07:00 AM' },
  { value: '08:00', label: '08:00 AM' },
  { value: '09:00', label: '09:00 AM' },
  { value: '10:00', label: '10:00 AM' },
  { value: '11:00', label: '11:00 AM' },
  { value: '12:00', label: '12:00 PM' },
  { value: '13:00', label: '01:00 PM' },
  { value: '14:00', label: '02:00 PM' },
  { value: '15:00', label: '03:00 PM' },
  { value: '16:00', label: '04:00 PM' },
  { value: '17:00', label: '05:00 PM' },
  { value: '18:00', label: '06:00 PM' },
  { value: '19:00', label: '07:00 PM' },
  { value: '20:00', label: '08:00 PM' },
  { value: '21:00', label: '09:00 PM' },
  { value: '22:00', label: '10:00 PM' },
  { value: '23:00', label: '11:00 PM' },
  { value: '24:00', label: '12:00 AM' },
];

function formatTimeDisplay(timeStr) {
  if (!timeStr) return '';
  const match = TIME_OPTIONS.find(o => o.value === timeStr);
  return match ? match.label : timeStr;
}

function MonthlySolarNonSolarMcpCard({ idPrefix = 'summary' }) {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  // Central segment selection (GDAM, DAM, RTM, Average)
  const [selectedSegments, setSelectedSegments] = useState(['GDAM']);
  const [rangeType, setRangeType] = useState('3y'); // '3y' | '1y' | 'all'
  const [viewMode, setViewMode] = useState('chart');
  const [expanded, setExpanded] = useState(false);

  // Solar Hours controls
  const [solarStart, setSolarStart] = useState('11:00');
  const [solarEnd, setSolarEnd] = useState('15:00');

  // Non-Solar Hours controls
  const [nonSolarStart, setNonSolarStart] = useState('18:00');
  const [nonSolarEnd, setNonSolarEnd] = useState('22:00');

  useEffect(() => {
    let isMounted = true;
    const fetchMcpData = async () => {
      setLoading(true);
      try {
        const res = await getMonthlySolarNonSolarMcp({
          range: rangeType,
          solar_start: solarStart,
          solar_end: solarEnd,
          non_solar_start: nonSolarStart,
          non_solar_end: nonSolarEnd,
        }).catch(() => null);
        const rows = res?.data?.results || [];
        if (isMounted) {
          setChartData(rows);
        }
      } catch (err) {
        console.error("Error fetching monthly solar & non-solar market price data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchMcpData();
    return () => { isMounted = false; };
  }, [rangeType, solarStart, solarEnd, nonSolarStart, nonSolarEnd]);

  const displayData = useMemo(() => {
    return chartData.map(item => {
      const solarVals = [item.solar_gdam_mcp, item.solar_dam_mcp, item.solar_rtm_mcp].filter(v => v != null && !isNaN(v));
      const solarAvg = item.solar_avg_mcp != null
        ? item.solar_avg_mcp
        : (solarVals.length > 0 ? Number((solarVals.reduce((a, b) => a + Number(b), 0) / solarVals.length).toFixed(2)) : null);

      const nonSolarVals = [item.non_solar_gdam_mcp, item.non_solar_dam_mcp, item.non_solar_rtm_mcp].filter(v => v != null && !isNaN(v));
      const nonSolarAvg = item.non_solar_avg_mcp != null
        ? item.non_solar_avg_mcp
        : (nonSolarVals.length > 0 ? Number((nonSolarVals.reduce((a, b) => a + Number(b), 0) / nonSolarVals.length).toFixed(2)) : null);

      return {
        ...item,
        solar_avg_mcp: solarAvg,
        non_solar_avg_mcp: nonSolarAvg,
        xTickLabel: item.label,
      };
    });
  }, [chartData]);

  const cardId = `chart-monthly-avg-solar-non-solar-mcp-${idPrefix}`;

  const solarLabel = `${formatTimeDisplay(solarStart)}–${formatTimeDisplay(solarEnd)}`;
  const nonSolarLabel = `${formatTimeDisplay(nonSolarStart)}–${formatTimeDisplay(nonSolarEnd)}`;

  // Unified Table Columns
  const tableColumns = [
    {
      title: 'Month',
      dataIndex: 'xTickLabel',
      key: 'xTickLabel',
      fixed: 'left',
      width: 120,
      render: (v, r) => <span style={{ textTransform: 'lowercase', fontWeight: 600 }}>{v} ({r.month})</span>
    },
    {
      title: `Solar (${solarLabel})`,
      children: [
        ...(selectedSegments.includes('GDAM') ? [{
          key: 'solar_gdam',
          title: 'GDAM MCP (₹/MWh)',
          dataIndex: 'solar_gdam_mcp',
          align: 'right',
          render: v => v != null ? `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'
        }] : []),
        ...(selectedSegments.includes('DAM') ? [{
          key: 'solar_dam',
          title: 'DAM MCP (₹/MWh)',
          dataIndex: 'solar_dam_mcp',
          align: 'right',
          render: v => v != null ? `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'
        }] : []),
        ...(selectedSegments.includes('RTM') ? [{
          key: 'solar_rtm',
          title: 'RTM MCP (₹/MWh)',
          dataIndex: 'solar_rtm_mcp',
          align: 'right',
          render: v => v != null ? `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'
        }] : []),
        ...(selectedSegments.includes('Average') ? [{
          key: 'solar_avg',
          title: 'Average MCP (₹/MWh)',
          dataIndex: 'solar_avg_mcp',
          align: 'right',
          render: v => v != null ? `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'
        }] : []),
      ]
    },
    {
      title: `Non-Solar (${nonSolarLabel})`,
      children: [
        ...(selectedSegments.includes('GDAM') ? [{
          key: 'non_solar_gdam',
          title: 'GDAM MCP (₹/MWh)',
          dataIndex: 'non_solar_gdam_mcp',
          align: 'right',
          render: v => v != null ? `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'
        }] : []),
        ...(selectedSegments.includes('DAM') ? [{
          key: 'non_solar_dam',
          title: 'DAM MCP (₹/MWh)',
          dataIndex: 'non_solar_dam_mcp',
          align: 'right',
          render: v => v != null ? `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'
        }] : []),
        ...(selectedSegments.includes('RTM') ? [{
          key: 'non_solar_rtm',
          title: 'RTM MCP (₹/MWh)',
          dataIndex: 'non_solar_rtm_mcp',
          align: 'right',
          render: v => v != null ? `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'
        }] : []),
        ...(selectedSegments.includes('Average') ? [{
          key: 'non_solar_avg',
          title: 'Average MCP (₹/MWh)',
          dataIndex: 'non_solar_avg_mcp',
          align: 'right',
          render: v => v != null ? `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'
        }] : []),
      ]
    }
  ];

  // CSV Export columns
  const csvColumns = [
    { key: 'month', title: 'Month', dataIndex: 'month' },
    { key: 'xTickLabel', title: 'Label', dataIndex: 'xTickLabel' },
    ...(selectedSegments.includes('GDAM') ? [{ key: 'solar_gdam', title: `GDAM MCP (${solarLabel})`, dataIndex: 'solar_gdam_mcp' }] : []),
    ...(selectedSegments.includes('DAM') ? [{ key: 'solar_dam', title: `DAM MCP (${solarLabel})`, dataIndex: 'solar_dam_mcp' }] : []),
    ...(selectedSegments.includes('RTM') ? [{ key: 'solar_rtm', title: `RTM MCP (${solarLabel})`, dataIndex: 'solar_rtm_mcp' }] : []),
    ...(selectedSegments.includes('Average') ? [{ key: 'solar_avg', title: `Average MCP (${solarLabel})`, dataIndex: 'solar_avg_mcp' }] : []),
    ...(selectedSegments.includes('GDAM') ? [{ key: 'non_solar_gdam', title: `GDAM MCP (${nonSolarLabel})`, dataIndex: 'non_solar_gdam_mcp' }] : []),
    ...(selectedSegments.includes('DAM') ? [{ key: 'non_solar_dam', title: `DAM MCP (${nonSolarLabel})`, dataIndex: 'non_solar_dam_mcp' }] : []),
    ...(selectedSegments.includes('RTM') ? [{ key: 'non_solar_rtm', title: `RTM MCP (${nonSolarLabel})`, dataIndex: 'non_solar_rtm_mcp' }] : []),
    ...(selectedSegments.includes('Average') ? [{ key: 'non_solar_avg', title: `Average MCP (${nonSolarLabel})`, dataIndex: 'non_solar_avg_mcp' }] : []),
  ];

  const xTickInterval = rangeType === '3y' ? 1 : (rangeType === 'all' ? 2 : 0);
  const chartHeight = expanded ? 'calc(100vh - 210px)' : 380;

  const renderMonthlyMcpLegend = (props) => {
    const { payload } = props;
    return (
      <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 10, flexWrap: 'wrap' }}>
        {payload.map((entry, index) => {
          const isDashed = entry.payload?.strokeDasharray || entry.value?.includes('Non-Solar');
          return (
            <span key={`legend-${index}`} style={{ display: 'inline-flex', alignItems: 'center', marginRight: 10 }}>
              <span style={{
                display: 'inline-block',
                width: 18,
                height: 0,
                borderTop: `3px ${isDashed ? 'dashed' : 'solid'} ${entry.color}`,
                marginRight: 6
              }} />
              <span style={{ fontSize: 12, color: '#475569', fontWeight: 500 }}>{entry.value}</span>
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <Card
      id={cardId}
      bordered={true}
      headStyle={MARKET_SUMMARY_CARD_HEAD_STYLE}
      style={expanded ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0, overflow: 'auto' } : { borderRadius: 8, background: '#fff', border: MARKET_SUMMARY_BOX_BORDER, marginBottom: 20 }}
      bodyStyle={{ padding: '16px 20px' }}
      title={
        <div style={{ fontSize: 16, fontWeight: 700, color: '#1B4F72' }}>
          Monthly Average Market Price
        </div>
      }
      extra={
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Segment Selection - clean checkboxes without dots or 'SEGMENTS:' label */}
          <Checkbox.Group
            options={['GDAM', 'DAM', 'RTM', 'Average']}
            value={selectedSegments}
            onChange={setSelectedSegments}
          />

          {/* Range Selector */}
          <Radio.Group value={rangeType} onChange={e => setRangeType(e.target.value)} size="small">
            <Radio.Button value="3y">3 Years</Radio.Button>
            <Radio.Button value="1y">1 Year</Radio.Button>
            <Radio.Button value="all">All</Radio.Button>
          </Radio.Group>

          {/* View Mode */}
          <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)} size="small">
            <Radio.Button value="chart">Chart</Radio.Button>
            <Radio.Button value="table">Table</Radio.Button>
          </Radio.Group>

          {viewMode === 'chart' ? (
            <DownloadButton onPng={() => exportCardAsPng(cardId, `Monthly_Avg_Market_Price_Solar_NonSolar_${rangeType.toUpperCase()}_${dayjs().format('MMMYYYY')}`)} />
          ) : (
            <DownloadButton onCsv={() => exportTableAsCsv(displayData, csvColumns, `Monthly_Avg_Market_Price_Solar_NonSolar_${rangeType.toUpperCase()}_${dayjs().format('MMMYYYY')}`)} />
          )}

          <Button icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpanded(!expanded)} />
        </div>
      }
    >
      <Spin spinning={loading}>
        {/* Sub-Header with Both Time Dropdowns Marked as Solar and Non-Solar */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 20,
          marginBottom: 12,
          paddingBottom: 10,
          borderBottom: '1px solid #E2E8F0',
          flexWrap: 'wrap'
        }}>
          {/* Solar Time Dropdowns */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Tag color="orange" style={{ fontWeight: 600, fontSize: 12, margin: 0, padding: '1px 8px', borderRadius: 4 }}>
              Solar
            </Tag>
            <span style={{ fontSize: 12, fontWeight: 500, color: '#64748B' }}>From</span>
            <Select
              size="small"
              style={{ width: 110 }}
              value={solarStart}
              onChange={setSolarStart}
              options={TIME_OPTIONS.slice(0, 24)}
            />
            <span style={{ fontSize: 12, fontWeight: 500, color: '#64748B' }}>To</span>
            <Select
              size="small"
              style={{ width: 110 }}
              value={solarEnd}
              onChange={setSolarEnd}
              options={TIME_OPTIONS.slice(1)}
            />
          </div>

          {/* Non-Solar Time Dropdowns */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Tag color="blue" style={{ fontWeight: 600, fontSize: 12, margin: 0, padding: '1px 8px', borderRadius: 4 }}>
              Non-Solar
            </Tag>
            <span style={{ fontSize: 12, fontWeight: 500, color: '#64748B' }}>From</span>
            <Select
              size="small"
              style={{ width: 110 }}
              value={nonSolarStart}
              onChange={setNonSolarStart}
              options={TIME_OPTIONS.slice(0, 24)}
            />
            <span style={{ fontSize: 12, fontWeight: 500, color: '#64748B' }}>To</span>
            <Select
              size="small"
              style={{ width: 110 }}
              value={nonSolarEnd}
              onChange={setNonSolarEnd}
              options={TIME_OPTIONS.slice(1)}
            />
          </div>
        </div>

        {viewMode === 'chart' ? (
          <div style={{
            border: '1px solid #E2E8F0',
            borderRadius: 8,
            background: '#FAFAFC',
            padding: '12px 14px',
            width: '100%'
          }}>
            <div style={{ height: chartHeight, width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={displayData} margin={{ left: 15, right: 20, bottom: 25, top: 12 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="xTickLabel"
                    tick={{ fontSize: 11, fill: '#475569' }}
                    interval={xTickInterval}
                    angle={-45}
                    textAnchor="end"
                    height={48}
                    tickFormatter={v => (v ? v.toLowerCase() : '')}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#475569' }}
                    tickFormatter={v => `₹${Math.round(Number(v)).toLocaleString('en-IN')}`}
                    domain={['auto', 'auto']}
                    label={{
                      value: 'Average MCP (₹/MWh)',
                      angle: -90,
                      position: 'insideLeft',
                      style: { fontSize: 11, fill: '#64748b' },
                      dy: 80,
                      dx: -8
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      borderRadius: 8,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      border: '1px solid #E2E8F0',
                      zIndex: 9999
                    }}
                    labelFormatter={(label, items) => {
                      const row = items?.[0]?.payload;
                      const m = row?.month ? ` (${row.month})` : '';
                      return `${label ? label.toLowerCase() : ''}${m} • Solar: ${solarLabel} | Non-Solar: ${nonSolarLabel}`;
                    }}
                    formatter={(val, name) => [
                      val != null ? `₹${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/MWh` : '-',
                      name
                    ]}
                  />
                  <Legend content={renderMonthlyMcpLegend} wrapperStyle={{ width: '100%', left: 0 }} />
                  <Brush dataKey="xTickLabel" height={22} stroke="#888" tickFormatter={v => (v ? v.toLowerCase() : '')} />

                  {/* GDAM Lines */}
                  {selectedSegments.includes('GDAM') && (
                    <Line
                      type="monotone"
                      dataKey="solar_gdam_mcp"
                      name="GDAM (Solar)"
                      stroke="#006400"
                      strokeWidth={2.2}
                      dot={false}
                      activeDot={{ r: 6 }}
                    />
                  )}
                  {selectedSegments.includes('GDAM') && (
                    <Line
                      type="monotone"
                      dataKey="non_solar_gdam_mcp"
                      name="GDAM (Non-Solar)"
                      stroke="#16A34A"
                      strokeWidth={2.2}
                      strokeDasharray="4 4"
                      dot={false}
                      activeDot={{ r: 6 }}
                    />
                  )}

                  {/* DAM Lines */}
                  {selectedSegments.includes('DAM') && (
                    <Line
                      type="monotone"
                      dataKey="solar_dam_mcp"
                      name="DAM (Solar)"
                      stroke="#00008B"
                      strokeWidth={2.2}
                      dot={false}
                      activeDot={{ r: 6 }}
                    />
                  )}
                  {selectedSegments.includes('DAM') && (
                    <Line
                      type="monotone"
                      dataKey="non_solar_dam_mcp"
                      name="DAM (Non-Solar)"
                      stroke="#2563EB"
                      strokeWidth={2.2}
                      strokeDasharray="4 4"
                      dot={false}
                      activeDot={{ r: 6 }}
                    />
                  )}

                  {/* RTM Lines */}
                  {selectedSegments.includes('RTM') && (
                    <Line
                      type="monotone"
                      dataKey="solar_rtm_mcp"
                      name="RTM (Solar)"
                      stroke="#8B0000"
                      strokeWidth={2.2}
                      dot={false}
                      activeDot={{ r: 6 }}
                    />
                  )}
                  {selectedSegments.includes('RTM') && (
                    <Line
                      type="monotone"
                      dataKey="non_solar_rtm_mcp"
                      name="RTM (Non-Solar)"
                      stroke="#DC2626"
                      strokeWidth={2.2}
                      strokeDasharray="4 4"
                      dot={false}
                      activeDot={{ r: 6 }}
                    />
                  )}

                  {/* Average Lines */}
                  {selectedSegments.includes('Average') && (
                    <Line
                      type="monotone"
                      dataKey="solar_avg_mcp"
                      name="Average (Solar)"
                      stroke="#6B21A8"
                      strokeWidth={2.2}
                      dot={false}
                      activeDot={{ r: 6 }}
                    />
                  )}
                  {selectedSegments.includes('Average') && (
                    <Line
                      type="monotone"
                      dataKey="non_solar_avg_mcp"
                      name="Average (Non-Solar)"
                      stroke="#9333EA"
                      strokeWidth={2.2}
                      strokeDasharray="4 4"
                      dot={false}
                      activeDot={{ r: 6 }}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          /* Table View: Single Unified Table */
          <div style={{ border: '1px solid #E2E8F0', borderRadius: 8, padding: 12, background: '#FAFAFC' }}>
            <Table
              dataSource={displayData}
              columns={tableColumns}
              size="small"
              scroll={{ x: 'max-content', y: 380 }}
              pagination={false}
              rowKey="month"
            />
          </div>
        )}
      </Spin>
    </Card>
  );
}

function SevenDaysFifteenMinMcpCard() {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [selectedSegments, setSelectedSegments] = useState(['GDAM', 'DAM', 'RTM']);
  const [viewMode, setViewMode] = useState('chart');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetch7DaysData = async () => {
      setLoading(true);
      try {
        const today = dayjs();
        const yesterday = today.subtract(1, 'day');
        const sevenDaysAgo = yesterday.subtract(6, 'day');

        const res = await getMarketDashboard({
          date_from: sevenDaysAgo.format('YYYY-MM-DD'),
          date_to: yesterday.format('YYYY-MM-DD'),
          granularity: '15min'
        }).catch(() => null);

        const rows = res?.data?.results || [];

        // Aggregate across the 7 days into 96 blocks (period: "HH:mm")
        const avgKeys = ['gdam_mcp', 'dam_mcp', 'rtm_mcp'];
        let aggregated = aggregateChartData(rows, '15min', avgKeys);

        if (!aggregated || aggregated.length < 96) {
          const blockMap = new Map((aggregated || []).map(item => [item.period, item]));
          const full96 = [];
          for (let b = 1; b <= 96; b++) {
            const totalMins = (b - 1) * 15;
            const h = String(Math.floor(totalMins / 60)).padStart(2, '0');
            const m = String(totalMins % 60).padStart(2, '0');
            const timeStr = `${h}:${m}`;

            const existing = blockMap.get(timeStr);
            const gdamVal = existing && existing.gdam_mcp != null ? Number(existing.gdam_mcp) : null;
            const damVal = existing && existing.dam_mcp != null ? Number(existing.dam_mcp) : null;
            const rtmVal = existing && existing.rtm_mcp != null ? Number(existing.rtm_mcp) : null;

            full96.push({
              period: timeStr,
              block_number: b,
              gdam_mcp: gdamVal != null ? Number(gdamVal.toFixed(2)) : null,
              dam_mcp: damVal != null ? Number(damVal.toFixed(2)) : null,
              rtm_mcp: rtmVal != null ? Number(rtmVal.toFixed(2)) : null,
            });
          }
          aggregated = full96;
        }

        if (isMounted) {
          setChartData(aggregated);
        }
      } catch (err) {
        console.error("Error fetching 7-day 15-min market data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetch7DaysData();
    return () => { isMounted = false; };
  }, []);

  const cardId = 'chart-7d-15min-mcp';

  const allColumns = [
    { title: 'Time Block', dataIndex: 'period', key: 'period', render: (v, r) => <span>{v} (Block {r.block_number})</span> },
    { key: 'GDAM', title: 'GDAM MCP (₹/kWh)', dataIndex: 'gdam_mcp', render: v => `₹${Number(v).toFixed(2)}` },
    { key: 'DAM', title: 'DAM MCP (₹/kWh)', dataIndex: 'dam_mcp', render: v => `₹${Number(v).toFixed(2)}` },
    { key: 'RTM', title: 'RTM MCP (₹/kWh)', dataIndex: 'rtm_mcp', render: v => `₹${Number(v).toFixed(2)}` },
  ];

  const tableColumns = allColumns.filter(c => !c.key || c.key === 'period' || selectedSegments.includes(c.key));

  return (
    <Card
      id={cardId}
      bordered={true}
      headStyle={MARKET_SUMMARY_CARD_HEAD_STYLE}
      style={expanded ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', border: MARKET_SUMMARY_BOX_BORDER, marginBottom: 16 }}
      bodyStyle={{ padding: '20px 24px' }}
      title={
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1B4F72' }}>
            15-Minute Aggregated MCP (Last 7 Days)
          </div>
        </div>
      }
      extra={
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Checkbox.Group
            options={['GDAM', 'DAM', 'RTM']}
            value={selectedSegments}
            onChange={setSelectedSegments}
          />
          <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)} size="small">
            <Radio.Button value="chart">Chart</Radio.Button>
            <Radio.Button value="table">Table</Radio.Button>
          </Radio.Group>
          {viewMode === 'chart' ? (
            <DownloadButton onPng={() => exportCardAsPng(cardId, `15Min_Aggregated_MCP_7D_${dayjs().format('DDMMMYYYY')}`)} />
          ) : (
            <DownloadButton onCsv={() => exportTableAsCsv(chartData, tableColumns, `15Min_Aggregated_MCP_7D_${dayjs().format('DDMMMYYYY')}`)} />
          )}
          <Button icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpanded(!expanded)} />
        </div>
      }
    >
      <Spin spinning={loading}>
        {viewMode === 'chart' ? (
          <div style={{ height: expanded ? 'calc(100vh - 140px)' : 380, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ left: 10, right: 25, bottom: 25, top: 15 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="period"
                  interval={3} // Exactly matches interval for 15min in Analysis tab
                  height={70}
                  tick={{ fontSize: 10, angle: 0, textAnchor: 'middle', dy: 10 }}
                  tickFormatter={v => v}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#475569' }}
                  tickFormatter={v => `₹${Math.round(Number(v))}`}
                  domain={['auto', 'auto']}
                  label={{
                    value: 'Price (₹/kWh)',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontSize: 11, fill: '#64748b' }
                  }}
                />
                <Tooltip
                  itemSorter={tooltipSorter}
                  labelFormatter={label => `Time: ${label}`}
                  formatter={(v, n) => [`₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/kWh`, n]}
                />
                <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 20 }} />
                <Brush dataKey="period" height={20} stroke="#888" />
                {selectedSegments.includes('GDAM') && (
                  <Line
                    type="monotone"
                    dataKey="gdam_mcp"
                    name="GDAM MCP"
                    stroke="#006400"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 5 }}
                  />
                )}
                {selectedSegments.includes('DAM') && (
                  <Line
                    type="monotone"
                    dataKey="dam_mcp"
                    name="DAM MCP"
                    stroke="#00008B"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 5 }}
                  />
                )}
                {selectedSegments.includes('RTM') && (
                  <Line
                    type="monotone"
                    dataKey="rtm_mcp"
                    name="RTM MCP"
                    stroke="#8B0000"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 5 }}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <Table
            dataSource={chartData}
            columns={tableColumns}
            size="small"
            scroll={{ x: 'max-content', y: 340 }}
            pagination={false}
            rowKey="period"
          />
        )}
      </Spin>
    </Card>
  );
}

function YesterdayFifteenMinMcpCard() {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [selectedSegments, setSelectedSegments] = useState(['GDAM', 'DAM', 'RTM']);
  const [viewMode, setViewMode] = useState('chart');
  const [expanded, setExpanded] = useState(false);

  const yesterday = useMemo(() => dayjs().subtract(1, 'day'), []);
  const yesterdayStr = yesterday.format('YYYY-MM-DD');
  const yesterdayLabel = yesterday.format('DD-MMM-YYYY');

  useEffect(() => {
    let isMounted = true;
    const fetchYesterdayData = async () => {
      setLoading(true);
      try {
        const res = await getMarketDashboard({
          date_from: yesterdayStr,
          date_to: yesterdayStr,
          granularity: '15min'
        }).catch(() => null);

        const rows = res?.data?.results || [];

        const avgKeys = ['gdam_mcp', 'dam_mcp', 'rtm_mcp'];
        let aggregated = aggregateChartData(rows, '15min', avgKeys);

        if (!aggregated || aggregated.length < 96) {
          const blockMap = new Map((aggregated || []).map(item => [item.period, item]));
          const full96 = [];
          for (let b = 1; b <= 96; b++) {
            const totalMins = (b - 1) * 15;
            const h = String(Math.floor(totalMins / 60)).padStart(2, '0');
            const m = String(totalMins % 60).padStart(2, '0');
            const timeStr = `${h}:${m}`;

            const existing = blockMap.get(timeStr);
            const gdamVal = existing && existing.gdam_mcp != null ? Number(existing.gdam_mcp) : null;
            const damVal = existing && existing.dam_mcp != null ? Number(existing.dam_mcp) : null;
            const rtmVal = existing && existing.rtm_mcp != null ? Number(existing.rtm_mcp) : null;

            full96.push({
              period: timeStr,
              block_number: b,
              gdam_mcp: gdamVal != null ? Number(gdamVal.toFixed(2)) : null,
              dam_mcp: damVal != null ? Number(damVal.toFixed(2)) : null,
              rtm_mcp: rtmVal != null ? Number(rtmVal.toFixed(2)) : null,
            });
          }
          aggregated = full96;
        }

        if (isMounted) {
          setChartData(aggregated);
        }
      } catch (err) {
        console.error("Error fetching yesterday's 15-min market data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchYesterdayData();
    return () => { isMounted = false; };
  }, [yesterdayStr]);

  const cardId = 'chart-yesterday-15min-mcp';

  const allColumns = [
    { title: 'Time Block', dataIndex: 'period', key: 'period', render: (v, r) => <span>{v} (Block {r.block_number})</span> },
    { key: 'GDAM', title: 'GDAM MCP (₹/kWh)', dataIndex: 'gdam_mcp', render: v => `₹${Number(v).toFixed(2)}` },
    { key: 'DAM', title: 'DAM MCP (₹/kWh)', dataIndex: 'dam_mcp', render: v => `₹${Number(v).toFixed(2)}` },
    { key: 'RTM', title: 'RTM MCP (₹/kWh)', dataIndex: 'rtm_mcp', render: v => `₹${Number(v).toFixed(2)}` },
  ];

  const tableColumns = allColumns.filter(c => !c.key || c.key === 'period' || selectedSegments.includes(c.key));

  return (
    <Card
      id={cardId}
      bordered={true}
      headStyle={MARKET_SUMMARY_CARD_HEAD_STYLE}
      style={expanded ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', border: MARKET_SUMMARY_BOX_BORDER, marginBottom: 16 }}
      bodyStyle={{ padding: '20px 24px' }}
      title={
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1B4F72' }}>
            15-Minute MCP ({yesterdayLabel})
          </div>
        </div>
      }
      extra={
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Checkbox.Group
            options={['GDAM', 'DAM', 'RTM']}
            value={selectedSegments}
            onChange={setSelectedSegments}
          />
          <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)} size="small">
            <Radio.Button value="chart">Chart</Radio.Button>
            <Radio.Button value="table">Table</Radio.Button>
          </Radio.Group>
          {viewMode === 'chart' ? (
            <DownloadButton onPng={() => exportCardAsPng(cardId, `15Min_MCP_Yesterday_${yesterday.format('DDMMMYYYY')}`)} />
          ) : (
            <DownloadButton onCsv={() => exportTableAsCsv(chartData, tableColumns, `15Min_MCP_Yesterday_${yesterday.format('DDMMMYYYY')}`)} />
          )}
          <Button icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpanded(!expanded)} />
        </div>
      }
    >
      <Spin spinning={loading}>
        {viewMode === 'chart' ? (
          <div style={{ height: expanded ? 'calc(100vh - 140px)' : 380, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ left: 10, right: 25, bottom: 25, top: 15 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="period"
                  interval={3}
                  height={70}
                  tick={{ fontSize: 10, angle: 0, textAnchor: 'middle', dy: 10 }}
                  tickFormatter={v => v}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#475569' }}
                  tickFormatter={v => `₹${Math.round(Number(v))}`}
                  domain={['auto', 'auto']}
                  label={{
                    value: 'Price (₹/kWh)',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontSize: 11, fill: '#64748b' }
                  }}
                />
                <Tooltip
                  itemSorter={tooltipSorter}
                  labelFormatter={label => `Time: ${label}`}
                  formatter={(v, n) => [`₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/kWh`, n]}
                />
                <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 20 }} />
                <Brush dataKey="period" height={20} stroke="#888" />
                {selectedSegments.includes('GDAM') && (
                  <Line
                    type="monotone"
                    dataKey="gdam_mcp"
                    name="GDAM MCP"
                    stroke="#006400"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 5 }}
                  />
                )}
                {selectedSegments.includes('DAM') && (
                  <Line
                    type="monotone"
                    dataKey="dam_mcp"
                    name="DAM MCP"
                    stroke="#00008B"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 5 }}
                  />
                )}
                {selectedSegments.includes('RTM') && (
                  <Line
                    type="monotone"
                    dataKey="rtm_mcp"
                    name="RTM MCP"
                    stroke="#8B0000"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 5 }}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <Table
            dataSource={chartData}
            columns={tableColumns}
            size="small"
            scroll={{ x: 'max-content', y: 340 }}
            pagination={false}
            rowKey="period"
          />
        )}
      </Spin>
    </Card>
  );
}

function MarketSegmentKpiTables() {
  const [loading, setLoading] = useState(false);
  const [kpiData, setKpiData] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchKpiData = async () => {
      setLoading(true);
      try {
        const today = dayjs();
        const yesterday = today.subtract(1, 'day');
        const mtdStart = yesterday.startOf('month');

        const curMonth = today.month();
        const fyYear = curMonth >= 3 ? today.year() : today.year() - 1;
        const ytdStart = dayjs(`${fyYear}-04-01`);

        const res = await getMarketDashboard({
          date_from: ytdStart.format('YYYY-MM-DD'),
          date_to: yesterday.format('YYYY-MM-DD'),
          granularity: 'daily'
        }).catch(() => null);

        const rows = res?.data?.results || [];
        const yesterdayStr = yesterday.format('YYYY-MM-DD');

        const computeSegmentStats = (segKey) => {
          const mcpKey = `${segKey}_mcp`;
          const purKey = `${segKey}_purchase_bid_sum`;
          const sellKey = `${segKey}_sell_bid_sum`;
          const purKeyAlt = `${segKey}_purchase_bid`;
          const sellKeyAlt = `${segKey}_sell_bid`;
          const mcvMwKey = `${segKey}_mcv_mw`;
          const mcvKeyAlt = `${segKey}_mcv`; // fallback if _mcv_mw is not present

          const getRowMcvMw = (r) => {
            if (!r) return null;
            if (r[mcvMwKey] != null && !isNaN(r[mcvMwKey])) return Number(r[mcvMwKey]);
            if (r[mcvKeyAlt] != null && !isNaN(r[mcvKeyAlt])) {
              // If only daily MU sum is present in older rows, convert back to average MW:
              // Daily MU = (MW * 0.25 * 96) / 1000 = MW * 0.024 => MW = MU / 0.024 = MU * 1000 / 24
              return (Number(r[mcvKeyAlt]) * 1000.0) / 24.0;
            }
            return null;
          };

          // 1. Yesterday
          const yRow = rows.find(r => r.date === yesterdayStr || (r.period && r.period.startsWith(yesterdayStr)));

          let yMcp = yRow && yRow[mcpKey] != null ? Number(yRow[mcpKey]) : null;
          let yPur = yRow && (yRow[purKey] != null || yRow[purKeyAlt] != null) ? Number(yRow[purKey] ?? yRow[purKeyAlt]) : null;
          let ySell = yRow && (yRow[sellKey] != null || yRow[sellKeyAlt] != null) ? Number(yRow[sellKey] ?? yRow[sellKeyAlt]) : null;
          let yVol = getRowMcvMw(yRow);
          let yRatio = (ySell != null && ySell > 0 && yPur != null) ? (yPur / ySell) : null;

          // 2. MTD
          const mtdRows = rows.filter(r => {
            const d = r.date || r.period;
            return d && d >= mtdStart.format('YYYY-MM-DD') && d <= yesterdayStr;
          });

          let mtdMcp = null;
          let mtdVol = null;
          let mtdRatio = null;

          if (mtdRows.length > 0) {
            const validMcps = mtdRows.map(r => r[mcpKey]).filter(v => v != null && !isNaN(v));
            if (validMcps.length > 0) {
              mtdMcp = validMcps.reduce((a, b) => a + Number(b), 0) / validMcps.length;
            }
            const totalPur = mtdRows.reduce((a, r) => a + Number(r[purKey] ?? r[purKeyAlt] ?? 0), 0);
            const totalSell = mtdRows.reduce((a, r) => a + Number(r[sellKey] ?? r[sellKeyAlt] ?? 0), 0);
            if (totalSell > 0) mtdRatio = totalPur / totalSell;

            const validVols = mtdRows.map(r => getRowMcvMw(r)).filter(v => v != null && !isNaN(v));
            if (validVols.length > 0) {
              mtdVol = validVols.reduce((a, b) => a + Number(b), 0) / validVols.length;
            }
          }

          // 3. YTD
          let ytdMcp = null;
          let ytdVol = null;
          let ytdRatio = null;

          if (rows.length > 0) {
            const validMcps = rows.map(r => r[mcpKey]).filter(v => v != null && !isNaN(v));
            if (validMcps.length > 0) {
              ytdMcp = validMcps.reduce((a, b) => a + Number(b), 0) / validMcps.length;
            }
            const totalPur = rows.reduce((a, r) => a + Number(r[purKey] ?? r[purKeyAlt] ?? 0), 0);
            const totalSell = rows.reduce((a, r) => a + Number(r[sellKey] ?? r[sellKeyAlt] ?? 0), 0);
            if (totalSell > 0) ytdRatio = totalPur / totalSell;

            const validVols = rows.map(r => getRowMcvMw(r)).filter(v => v != null && !isNaN(v));
            if (validVols.length > 0) {
              ytdVol = validVols.reduce((a, b) => a + Number(b), 0) / validVols.length;
            }
          }

          return {
            yesterday: {
              mcp: yMcp != null ? yMcp.toFixed(2) : '-',
              volume: yVol != null ? Number(yVol).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-',
              ratio: yRatio != null ? yRatio.toFixed(2) : '-'
            },
            mtd: {
              mcp: mtdMcp != null ? mtdMcp.toFixed(2) : '-',
              volume: mtdVol != null ? Number(mtdVol).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-',
              ratio: mtdRatio != null ? mtdRatio.toFixed(2) : '-'
            },
            ytd: {
              mcp: ytdMcp != null ? ytdMcp.toFixed(2) : '-',
              volume: ytdVol != null ? Number(ytdVol).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-',
              ratio: ytdRatio != null ? ytdRatio.toFixed(2) : '-'
            }
          };
        };

        const data = {
          DAM: computeSegmentStats('dam'),
          GDAM: computeSegmentStats('gdam'),
          RTM: computeSegmentStats('rtm'),
        };

        if (isMounted) {
          setKpiData(data);
        }
      } catch (err) {
        console.error("Error fetching segment KPI data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchKpiData();
    return () => { isMounted = false; };
  }, []);

  const segments = ['DAM', 'GDAM', 'RTM'];
  const metrics = [
    { key: 'mcp', label: 'MCP (₹/kWh)' },
    { key: 'volume', label: 'MCV (MW)' },
    { key: 'ratio', label: 'Buy / Sell Ratio' },
  ];
  const yesterdayDateLabel = dayjs().subtract(1, 'day').format('DD-MM-YYYY');

  const cellBorderColor = '#cbd5e1';
  const groupBorderColor = '#94a3b8';

  return (
    <Spin spinning={loading}>
      <div style={{ marginBottom: 24 }}>
        <div
          style={{
            background: '#ffffff',
            border: MARKET_SUMMARY_BOX_BORDER,
            borderRadius: 8,
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, color: '#1B4F72' }}>
            <thead>
              <tr style={{ backgroundColor: '#ebf3fb', borderBottom: `1px solid ${cellBorderColor}` }}>
                <th
                  rowSpan={2}
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontWeight: 700,
                    color: '#1B4F72',
                    width: '16%',
                    borderRight: `1px solid ${groupBorderColor}`,
                    verticalAlign: 'middle',
                    fontSize: 14
                  }}
                >
                  Metric
                </th>
                {segments.map((seg, idx) => (
                  <th
                    key={seg}
                    colSpan={3}
                    style={{
                      padding: '10px 12px',
                      textAlign: 'center',
                      fontWeight: 700,
                      color: '#1B4F72',
                      fontSize: 14,
                      borderRight: idx < segments.length - 1 ? `1px solid ${groupBorderColor}` : 'none',
                      borderBottom: `1px solid ${cellBorderColor}`
                    }}
                  >
                    {seg}
                  </th>
                ))}
              </tr>
              <tr style={{ backgroundColor: '#ebf3fb', borderBottom: `1px solid ${cellBorderColor}` }}>
                {segments.map((seg, segIdx) => (
                  <React.Fragment key={`${seg}-subheaders`}>
                    <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: '#1B4F72', width: '9.3%', borderRight: `1px solid ${cellBorderColor}` }}>
                      {yesterdayDateLabel}
                    </th>
                    <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: '#1B4F72', width: '9.3%', borderRight: `1px solid ${cellBorderColor}` }}>
                      MTD
                    </th>
                    <th
                      style={{
                        padding: '8px 10px',
                        textAlign: 'center',
                        fontWeight: 700,
                        color: '#1B4F72',
                        width: '9.3%',
                        borderRight: segIdx < segments.length - 1 ? `1px solid ${groupBorderColor}` : 'none'
                      }}
                    >
                      YTD
                    </th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {metrics.map((m, mIdx) => (
                <tr key={m.key} style={{ borderBottom: mIdx < metrics.length - 1 ? `1px solid ${cellBorderColor}` : 'none' }}>
                  <td
                    style={{
                      padding: '12px 16px',
                      fontWeight: 700,
                      color: '#1B4F72',
                      borderRight: `1px solid ${groupBorderColor}`,
                      backgroundColor: '#ffffff'
                    }}
                  >
                    {m.label}
                  </td>
                  {segments.map((seg, segIdx) => {
                    const stats = kpiData?.[seg];
                    return (
                      <React.Fragment key={`${seg}-${m.key}`}>
                        <td style={{ padding: '11px 12px', textAlign: 'center', color: '#0f172a', fontWeight: 500, borderRight: `1px solid ${cellBorderColor}` }}>
                          {stats ? stats.yesterday[m.key] : '-'}
                        </td>
                        <td style={{ padding: '11px 12px', textAlign: 'center', color: '#0f172a', fontWeight: 500, borderRight: `1px solid ${cellBorderColor}` }}>
                          {stats ? stats.mtd[m.key] : '-'}
                        </td>
                        <td
                          style={{
                            padding: '11px 12px',
                            textAlign: 'center',
                            color: '#0f172a',
                            fontWeight: 500,
                            borderRight: segIdx < segments.length - 1 ? `1px solid ${groupBorderColor}` : 'none'
                          }}
                        >
                          {stats ? stats.ytd[m.key] : '-'}
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Spin>
  );
}


function MarketSummaryTab() {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [selectedSegments, setSelectedSegments] = useState(['GDAM', 'DAM', 'RTM']);
  const [viewMode, setViewMode] = useState('chart');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetch3YearData = async () => {
      setLoading(true);
      try {
        const today = dayjs();
        // Rolling 36 months (Last 3 Years) ending at the current month
        const baseDate = today.subtract(35, 'month').startOf('month');
        const startDate = baseDate.format('YYYY-MM-DD');
        const endDate = today.endOf('month').format('YYYY-MM-DD');

        const res = await getMarketDashboard({
          date_from: startDate,
          date_to: endDate,
          granularity: 'monthly'
        }).catch(() => null);

        const rows = res?.data?.results || [];

        // Build continuous 36-month array ending at the current month
        const months = [];

        for (let i = 0; i < 36; i++) {
          const cur = baseDate.add(i, 'month');
          const monthKey = cur.format('YYYY-MM');
          // x-axis format as requested: aug-24, sep-24 etc.
          const xTickLabel = cur.format('MMM-YY').toLowerCase();

          const dbMatch = rows.find(r => r.period && r.period.startsWith(monthKey));

          let gdamVal = dbMatch && dbMatch.gdam_mcp != null ? Number(dbMatch.gdam_mcp) : null;
          let damVal = dbMatch && dbMatch.dam_mcp != null ? Number(dbMatch.dam_mcp) : null;
          let rtmVal = dbMatch && dbMatch.rtm_mcp != null ? Number(dbMatch.rtm_mcp) : null;

          months.push({
            period: monthKey,
            xTickLabel,
            gdam_mcp: gdamVal != null ? Number(gdamVal.toFixed(2)) : null,
            dam_mcp: damVal != null ? Number(damVal.toFixed(2)) : null,
            rtm_mcp: rtmVal != null ? Number(rtmVal.toFixed(2)) : null,
          });
        }

        if (isMounted) {
          setChartData(months);
        }
      } catch (err) {
        console.error("Error fetching 3-year market summary data:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetch3YearData();
    return () => { isMounted = false; };
  }, []);

  const allColumns = [
    { title: 'Month', dataIndex: 'xTickLabel', key: 'xTickLabel', render: v => <span style={{ textTransform: 'lowercase', fontWeight: 600 }}>{v}</span> },
    { key: 'GDAM', title: 'GDAM MCP (₹/kWh)', dataIndex: 'gdam_mcp', render: v => `₹${Number(v).toFixed(2)}` },
    { key: 'DAM', title: 'DAM MCP (₹/kWh)', dataIndex: 'dam_mcp', render: v => `₹${Number(v).toFixed(2)}` },
    { key: 'RTM', title: 'RTM MCP (₹/kWh)', dataIndex: 'rtm_mcp', render: v => `₹${Number(v).toFixed(2)}` },
  ];

  const tableColumns = allColumns.filter(c => !c.key || c.key === 'xTickLabel' || selectedSegments.includes(c.key));

  return (
    <Spin spinning={loading}>
      <div style={{ marginBottom: 20 }}>
        {/* KPIs Table for all 3 market segments */}
        <Row gutter={[20, 20]}>
          <Col xs={24}>
            <MarketSegmentKpiTables />
          </Col>
        </Row>

        <Row gutter={[20, 20]}>
          <Col xs={24}>
            <Card
              id="chart-3y-monthly-mcp"
              bordered={true}
              headStyle={MARKET_SUMMARY_CARD_HEAD_STYLE}
              style={expanded ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', border: MARKET_SUMMARY_BOX_BORDER, marginBottom: 20 }}
              bodyStyle={{ padding: '20px 24px' }}
              title={
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#1B4F72' }}>
                    Monthly Average MCP (Last 3 Years)
                  </div>
                </div>
              }
              extra={
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <Checkbox.Group
                    options={['GDAM', 'DAM', 'RTM']}
                    value={selectedSegments}
                    onChange={setSelectedSegments}
                  />
                  <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)} size="small">
                    <Radio.Button value="chart">Chart</Radio.Button>
                    <Radio.Button value="table">Table</Radio.Button>
                  </Radio.Group>
                  {viewMode === 'chart' ? (
                    <DownloadButton onPng={() => exportCardAsPng('chart-3y-monthly-mcp', `Monthly_Avg_MCP_3Y_${dayjs().format('MMMYYYY')}`)} />
                  ) : (
                    <DownloadButton onCsv={() => exportTableAsCsv(chartData, tableColumns, `Monthly_Avg_MCP_3Y_${dayjs().format('MMMYYYY')}`)} />
                  )}
                  <Button icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpanded(!expanded)} />
                </div>
              }
            >
              {viewMode === 'chart' ? (
                <div style={{ height: expanded ? 'calc(100vh - 140px)' : 380, width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ left: 10, right: 25, bottom: 25, top: 15 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        dataKey="xTickLabel"
                        tick={{ fontSize: 11, fill: '#475569' }}
                        interval={0}
                        angle={-45}
                        textAnchor="end"
                        height={50}
                        tickFormatter={v => (v ? v.toLowerCase() : '')}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: '#475569' }}
                        tickFormatter={v => `₹${Math.round(Number(v))}`}
                        domain={['auto', 'auto']}
                        label={{
                          value: 'MCP (₹/kWh)',
                          angle: -90,
                          position: 'insideLeft',
                          style: { fontSize: 12, fill: '#64748b' }
                        }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#ffffff',
                          borderRadius: 8,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          border: 'none',
                          zIndex: 9999
                        }}
                        labelFormatter={label => `Month: ${label ? label.toLowerCase() : ''}`}
                        formatter={(val, name) => [val != null ? `₹${Number(val).toFixed(2)}/kWh` : '-', name]}
                      />
                      <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 20 }} />
                      <Brush dataKey="xTickLabel" height={22} stroke="#888" />
                      {selectedSegments.includes('GDAM') && (
                        <Line
                          type="monotone"
                          dataKey="gdam_mcp"
                          name="GDAM MCP"
                          stroke="#006400"
                          strokeWidth={2.2}
                          dot={false}
                          activeDot={{ r: 6 }}
                        />
                      )}
                      {selectedSegments.includes('DAM') && (
                        <Line
                          type="monotone"
                          dataKey="dam_mcp"
                          name="DAM MCP"
                          stroke="#00008B"
                          strokeWidth={2.2}
                          dot={false}
                          activeDot={{ r: 6 }}
                        />
                      )}
                      {selectedSegments.includes('RTM') && (
                        <Line
                          type="monotone"
                          dataKey="rtm_mcp"
                          name="RTM MCP"
                          stroke="#8B0000"
                          strokeWidth={2.2}
                          dot={false}
                          activeDot={{ r: 6 }}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <Table
                  dataSource={chartData}
                  columns={tableColumns}
                  size="small"
                  scroll={{ x: 'max-content', y: 340 }}
                  pagination={false}
                  rowKey="period"
                />
              )}
            </Card>
          </Col>
        </Row>


        {/* 2nd Graph: Last 30 days daily average MCP */}
        <Row gutter={[20, 20]}>
          <Col xs={24}>
            <ThirtyDaysDailyMcpCard idPrefix="summary" />
          </Col>
        </Row>

        {/* New Graph: Last 30 days daily average MCV */}
        <Row gutter={[20, 20]}>
          <Col xs={24}>
            <ThirtyDaysDailyMcvCard idPrefix="summary" />
          </Col>
        </Row>

        {/* 3rd Graph: Last 30 days Purchase bid / sell bid ratio */}
        <Row gutter={[20, 20]}>
          <Col xs={24}>
            <ThirtyDaysBidRatioCard />
          </Col>
        </Row>

        {/* 4th Graph: Last 7 days 15-minute aggregated MCP */}
        <Row gutter={[20, 20]}>
          <Col xs={24}>
            <SevenDaysFifteenMinMcpCard />
          </Col>
        </Row>

        {/* 15-Minute MCP (Yesterday) */}
        <Row gutter={[20, 20]}>
          <Col xs={24}>
            <YesterdayFifteenMinMcpCard />
          </Col>
        </Row>


        {/* 6th Graph: Last 30 days Our realization (PU) vs. market (weighted average of market) */}
        <Row gutter={[20, 20]}>
          <Col xs={24}>
            <ThirtyDaysRealizationVsMarketCard idPrefix="summary" />
          </Col>
        </Row>

        {/* 7th Graph: Total Outage (YTD) */}
        <Row gutter={[20, 20]}>
          <Col xs={24}>
            <TotalOutageYtdCard idPrefix="summary" />
          </Col>
        </Row>

        {/* 8th Graph: Peak Demand Met (YTD) */}
        <Row gutter={[20, 20]}>
          <Col xs={24}>
            <PeakDemandMetYtdCard idPrefix="summary" />
          </Col>
        </Row>

        {/* 9th Graph: Renewable Energy (YTD) */}
        <Row gutter={[20, 20]}>
          <Col xs={24}>
            <RenewableEnergyYtdCard idPrefix="summary" />
          </Col>
        </Row>

        {/* 10th Graph: % of Actual vs Normative Stock (Coal) */}
        <Row gutter={[20, 20]}>
          <Col xs={24}>
            <CoalStockActualVsNormativeCard idPrefix="summary" />
          </Col>
        </Row>

        {/* 11th Graph: Present Reservoir levels */}
        <Row gutter={[20, 20]}>
          <Col xs={24}>
            <PresentReservoirLevelsCard idPrefix="summary" />
          </Col>
        </Row>

        {/* 12th Graph: Monthly Average Market Price (solar & non-solar hrs) */}
        <Row gutter={[20, 20]}>
          <Col xs={24}>
            <MonthlySolarNonSolarMcpCard idPrefix="summary" />
          </Col>
        </Row>
      </div>
    </Spin>
  );
}

export default function MarketDataDashboard() {
  const f = useFilters();
  const [activeTab, setActiveTab] = useState('summary');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedChart, setExpandedChart] = useState(null);

  const isCurrentDateSelected = useMemo(() => {
    const todayStr = dayjs().format('YYYY-MM-DD');
    const fromStr = f.dateFrom ? dayjs(f.dateFrom).format('YYYY-MM-DD') : null;
    const toStr = f.dateTo ? dayjs(f.dateTo).format('YYYY-MM-DD') : null;
    return fromStr === todayStr || toStr === todayStr;
  }, [f.dateFrom, f.dateTo]);

  // View modes: 'chart' or 'table'
  const [view1, setView1] = useState('chart');
  const [view2, setView2] = useState('chart');

  // Segments for MCP-MCV chart
  const [segments, setSegments] = useState(['GDAM', 'DAM', 'RTM']);

  // Metrics for Chart 2
  const [metrics2, setMetrics2] = useState(['gross_rev_exch', 'gross_tariff_realized_plant', 'gross_tariff_realized_exch']);

  // Aggregate Toggle
  const [showAggregate, setShowAggregate] = useState(false);

  // Chart 3 States
  const [view3, setView3] = useState('chart');
  const [metrics3, setMetrics3] = useState('RTM/GDAM');

  // Buy / Sell Bid Ratio States
  const [viewBidRatio, setViewBidRatio] = useState('chart');
  const [segmentsBidRatio, setSegmentsBidRatio] = useState(['GDAM', 'DAM', 'RTM']);

  const fetchData = () => {
    setLoading(true);
    getMarketDashboard(f.apiParams(true))
      .then(res => {
        const avgKeys = ['gdam_mcp', 'dam_mcp', 'rtm_mcp', 'gross_rev_exch', 'gross_rev_plant', 'gdam_acp', 'dam_acp', 'rtm_acp', 'dam_plant_realisation', 'gdam_plant_realisation', 'rtm_plant_realisation'];
        let aggregated = aggregateChartData(res.data.results || [], f.granularity, avgKeys);
        aggregated = aggregated.map(item => {
          const final_schedule_mw_sum = Number(item.rdm_final_schedule_mw_sum) || 0;
          const gross_rev_plant_sum = Number(item.rdm_gross_revenue_kwh_sum) || 0;
          const total_exchange_cleared_vol_sum = Number(item.total_exch_cleared_vol_sum) || 0;
          const exchange_gross_revenue_sum = Number(item.exchange_gross_revenue_sum) || 0;

          const damAcp = item.dam_acp != null ? Number(item.dam_acp) : (item.dam_pu != null ? Number(item.dam_pu) : (item.dam_mcp != null ? Number(item.dam_mcp) : null));
          const gdamAcp = item.gdam_acp != null ? Number(item.gdam_acp) : (item.gdam_pu != null ? Number(item.gdam_pu) : (item.gdam_mcp != null ? Number(item.gdam_mcp) : null));
          const rtmAcp = item.rtm_acp != null ? Number(item.rtm_acp) : (item.rtm_pu != null ? Number(item.rtm_pu) : (item.rtm_mcp != null ? Number(item.rtm_mcp) : null));

          return {
            ...item,
            dam_plant_realisation: damAcp != null ? Number(damAcp.toFixed(2)) : null,
            gdam_plant_realisation: gdamAcp != null ? Number(gdamAcp.toFixed(2)) : null,
            rtm_plant_realisation: rtmAcp != null ? Number(rtmAcp.toFixed(2)) : null,
            gross_tariff_realized_plant: final_schedule_mw_sum === 0 ? (item.gross_tariff_realized_plant ?? null) : gross_rev_plant_sum / (final_schedule_mw_sum * 250.0),
            gross_tariff_realized_exch: total_exchange_cleared_vol_sum === 0 ? (item.gross_tariff_realized_exch ?? null) : exchange_gross_revenue_sum / total_exchange_cleared_vol_sum
          };
        });
        setData(aggregated);
        setLoading(false);
      })
      .catch(err => {
        console.error("Error fetching market dashboard data:", err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (expandedChart) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [expandedChart]);


  const kpis = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        avg_mcp_gdam: 0,
        avg_mcp_dam: 0,
        avg_mcp_rtm: 0,
        total_mcv_gdam: 0,
        total_mcv_dam: 0,
        total_mcv_rtm: 0,
      };
    }

    let sumGdamMcp = 0, countGdamMcp = 0;
    let sumDamMcp = 0, countDamMcp = 0;
    let sumRtmMcp = 0, countRtmMcp = 0;
    let sumGdamMcv = 0;
    let sumDamMcv = 0;
    let sumRtmMcv = 0;

    data.forEach(d => {
      if (d.gdam_mcp != null) {
        sumGdamMcp += Number(d.gdam_mcp);
        countGdamMcp++;
      }
      if (d.dam_mcp != null) {
        sumDamMcp += Number(d.dam_mcp);
        countDamMcp++;
      }
      if (d.rtm_mcp != null) {
        sumRtmMcp += Number(d.rtm_mcp);
        countRtmMcp++;
      }
      if (d.gdam_mcv != null) {
        sumGdamMcv += Number(d.gdam_mcv);
      }
      if (d.dam_mcv != null) {
        sumDamMcv += Number(d.dam_mcv);
      }
      if (d.rtm_mcv != null) {
        sumRtmMcv += Number(d.rtm_mcv);
      }
    });

    return {
      avg_mcp_gdam: countGdamMcp > 0 ? sumGdamMcp / countGdamMcp : 0,
      avg_mcp_dam: countDamMcp > 0 ? sumDamMcp / countDamMcp : 0,
      avg_mcp_rtm: countRtmMcp > 0 ? sumRtmMcp / countRtmMcp : 0,
      total_mcv_gdam: sumGdamMcv,
      total_mcv_dam: sumDamMcv,
      total_mcv_rtm: sumRtmMcv,
    };
  }, [data]);

  const filterSummary = `${f.dateFrom?.format('DD-MMM-YYYY') || ''} to ${f.dateTo?.format('DD-MMM-YYYY') || ''}, ${f.granularity}`;

  const columns1 = useMemo(() => getTableColumns(f.granularity, [
    { title: 'GDAM MCP (Rs/kWh)', dataIndex: 'gdam_mcp', key: 'gdam_mcp', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' },
    { title: 'DAM MCP (Rs/kWh)', dataIndex: 'dam_mcp', key: 'dam_mcp', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' },
    { title: 'RTM MCP (Rs/kWh)', dataIndex: 'rtm_mcp', key: 'rtm_mcp', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' },
    { title: 'GDAM MCV (MU)', dataIndex: 'gdam_mcv', key: 'gdam_mcv', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' },
    { title: 'DAM MCV (MU)', dataIndex: 'dam_mcv', key: 'dam_mcv', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' },
    { title: 'RTM MCV (MU)', dataIndex: 'rtm_mcv', key: 'rtm_mcv', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' },
  ]), [f.granularity]);

  const columns2 = useMemo(() => getTableColumns(f.granularity, [
    ...(metrics2.includes('gdam_mcp') ? [{ title: 'GDAM MCP', dataIndex: 'gdam_mcp', key: 'gdam_mcp', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' }] : []),
    ...(metrics2.includes('dam_mcp') ? [{ title: 'DAM MCP', dataIndex: 'dam_mcp', key: 'dam_mcp', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' }] : []),
    ...(metrics2.includes('rtm_mcp') ? [{ title: 'RTM MCP', dataIndex: 'rtm_mcp', key: 'rtm_mcp', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' }] : []),
    ...(metrics2.includes('dam_plant_realisation') ? [{ title: 'DAM Plant Realisation', dataIndex: 'dam_plant_realisation', key: 'dam_plant_realisation', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' }] : []),
    ...(metrics2.includes('gdam_plant_realisation') ? [{ title: 'GDAM Plant Realisation', dataIndex: 'gdam_plant_realisation', key: 'gdam_plant_realisation', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' }] : []),
    ...(metrics2.includes('rtm_plant_realisation') ? [{ title: 'RTM Plant Realisation', dataIndex: 'rtm_plant_realisation', key: 'rtm_plant_realisation', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' }] : []),
    ...(metrics2.includes('gross_rev_exch') ? [{ title: 'Wtd Avg MCP', dataIndex: 'gross_rev_exch', key: 'gross_rev_exch', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' }] : []),
    ...(metrics2.includes('gross_tariff_realized_plant') ? [{ title: 'Gross Plant Realisation', dataIndex: 'gross_tariff_realized_plant', key: 'gross_tariff_realized_plant', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' }] : []),
    ...(metrics2.includes('gross_tariff_realized_exch') ? [{ title: 'Gross Tariff Exchange', dataIndex: 'gross_tariff_realized_exch', key: 'gross_tariff_realized_exch', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' }] : []),
  ]), [f.granularity, metrics2]);

  const tableData1 = useMemo(() => formatTableData(data, f.granularity), [data, f.granularity]);
  const tableData2 = useMemo(() => formatTableData(data, f.granularity), [data, f.granularity]);

  const chart3Data = useMemo(() => {
    const safeRatio = (num, den) => {
      if (den < 0.05) return null;
      const ratio = num / den;
      return ratio <= 20 ? ratio : null;
    };

    return data.map(d => {
      const gdam = Number(d.gdam_mcp) || 0;
      const rtm = Number(d.rtm_mcp) || 0;
      const dam = Number(d.dam_mcp) || 0;

      return {
        period: d.period,
        date: d.date,
        block_number: d.block_number,
        'GDAM/RTM': safeRatio(gdam, rtm),
        'RTM/DAM': safeRatio(rtm, dam),
        'DAM/GDAM': safeRatio(dam, gdam),
        'RTM/GDAM': safeRatio(rtm, gdam),
      };
    });
  }, [data]);

  const columns3 = useMemo(() => getTableColumns(f.granularity, [
    ...(metrics3 === 'GDAM/RTM' ? [{ title: 'GDAM/RTM Ratio', dataIndex: 'GDAM/RTM', render: v => v != null && typeof v === 'number' ? v.toFixed(3) : v ?? '-' }] : []),
    ...(metrics3 === 'RTM/DAM' ? [{ title: 'RTM/DAM Ratio', dataIndex: 'RTM/DAM', render: v => v != null && typeof v === 'number' ? v.toFixed(3) : v ?? '-' }] : []),
    ...(metrics3 === 'DAM/GDAM' ? [{ title: 'DAM/GDAM Ratio', dataIndex: 'DAM/GDAM', render: v => v != null && typeof v === 'number' ? v.toFixed(3) : v ?? '-' }] : []),
    ...(metrics3 === 'RTM/GDAM' ? [{ title: 'RTM/GDAM Ratio', dataIndex: 'RTM/GDAM', render: v => v != null && typeof v === 'number' ? v.toFixed(3) : v ?? '-' }] : []),
  ]), [f.granularity, metrics3]);

  const tableData3 = useMemo(() => formatTableData(chart3Data, f.granularity), [chart3Data, f.granularity]);

  const chartBidRatioData = useMemo(() => {
    const calcRatio = (pur, sell) => {
      if (pur == null || sell == null) return null;
      const absPur = Math.abs(pur);
      const absSell = Math.abs(sell);
      if (absSell > 0) {
        const r = absPur / absSell;
        return Number(r.toFixed(3));
      }
      return null;
    };

    return data.map(d => {
      const damPur = d.dam_purchase_bid_sum != null || d.dam_purchase_bid != null ? Number(d.dam_purchase_bid_sum ?? d.dam_purchase_bid) : null;
      const damSell = d.dam_sell_bid_sum != null || d.dam_sell_bid != null ? Number(d.dam_sell_bid_sum ?? d.dam_sell_bid) : null;

      const gdamPur = d.gdam_purchase_bid_sum != null || d.gdam_purchase_bid != null ? Number(d.gdam_purchase_bid_sum ?? d.gdam_purchase_bid) : null;
      const gdamSell = d.gdam_sell_bid_sum != null || d.gdam_sell_bid != null ? Number(d.gdam_sell_bid_sum ?? d.gdam_sell_bid) : null;

      const rtmPur = d.rtm_purchase_bid_sum != null || d.rtm_purchase_bid != null ? Number(d.rtm_purchase_bid_sum ?? d.rtm_purchase_bid) : null;
      const rtmSell = d.rtm_sell_bid_sum != null || d.rtm_sell_bid != null ? Number(d.rtm_sell_bid_sum ?? d.rtm_sell_bid) : null;

      return {
        ...d,
        period: d.period,
        date: d.date,
        block_number: d.block_number,
        DAM: calcRatio(damPur, damSell),
        GDAM: calcRatio(gdamPur, gdamSell),
        RTM: calcRatio(rtmPur, rtmSell),
        damPur: damPur != null ? Number(damPur.toFixed(1)) : null,
        damSell: damSell != null ? Number(damSell.toFixed(1)) : null,
        gdamPur: gdamPur != null ? Number(gdamPur.toFixed(1)) : null,
        gdamSell: gdamSell != null ? Number(gdamSell.toFixed(1)) : null,
        rtmPur: rtmPur != null ? Number(rtmPur.toFixed(1)) : null,
        rtmSell: rtmSell != null ? Number(rtmSell.toFixed(1)) : null,
      };
    });
  }, [data]);

  const columnsBidRatio = useMemo(() => getTableColumns(f.granularity, [
    ...(segmentsBidRatio.includes('DAM') ? [
      { title: 'DAM Ratio', dataIndex: 'DAM', key: 'damRatio', render: v => v != null && typeof v === 'number' ? v.toFixed(3) : v ?? '-' },
      { title: 'DAM Pur (MW)', dataIndex: 'damPur', key: 'damPur', render: v => v != null && typeof v === 'number' ? v.toFixed(1) : v ?? '-' },
      { title: 'DAM Sell (MW)', dataIndex: 'damSell', key: 'damSell', render: v => v != null && typeof v === 'number' ? v.toFixed(1) : v ?? '-' },
    ] : []),
    ...(segmentsBidRatio.includes('GDAM') ? [
      { title: 'GDAM Ratio', dataIndex: 'GDAM', key: 'gdamRatio', render: v => v != null && typeof v === 'number' ? v.toFixed(3) : v ?? '-' },
      { title: 'GDAM Pur (MW)', dataIndex: 'gdamPur', key: 'gdamPur', render: v => v != null && typeof v === 'number' ? v.toFixed(1) : v ?? '-' },
      { title: 'GDAM Sell (MW)', dataIndex: 'gdamSell', key: 'gdamSell', render: v => v != null && typeof v === 'number' ? v.toFixed(1) : v ?? '-' },
    ] : []),
    ...(segmentsBidRatio.includes('RTM') ? [
      { title: 'RTM Ratio', dataIndex: 'RTM', key: 'rtmRatio', render: v => v != null && typeof v === 'number' ? v.toFixed(3) : v ?? '-' },
      { title: 'RTM Pur (MW)', dataIndex: 'rtmPur', key: 'rtmPur', render: v => v != null && typeof v === 'number' ? v.toFixed(1) : v ?? '-' },
      { title: 'RTM Sell (MW)', dataIndex: 'rtmSell', key: 'rtmSell', render: v => v != null && typeof v === 'number' ? v.toFixed(1) : v ?? '-' },
    ] : []),
  ]), [f.granularity, segmentsBidRatio]);

  const tableDataBidRatio = useMemo(() => formatTableData(chartBidRatioData, f.granularity), [chartBidRatioData, f.granularity]);

  const mcpRatioStats = useMemo(() => {
    const stats = {};
    ['RTM/GDAM', 'GDAM/RTM', 'RTM/DAM', 'DAM/GDAM'].forEach(ratio => {
      const values = chart3Data
        .map(d => d[ratio])
        .filter(v => v !== null && v !== undefined && !isNaN(v));

      if (values.length > 0) {
        const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
        const variance = values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / values.length;
        const stdDev = Math.sqrt(variance);
        stats[ratio] = { mean, stdDev };
      } else {
        stats[ratio] = { mean: 0, stdDev: 0 };
      }
    });
    return stats;
  }, [chart3Data]);

  const formatRatioStat = (val) => {
    if (val === null || val === undefined || isNaN(val)) return '-';
    return val.toFixed(2);
  };

  const activeRatioStats = useMemo(() => {
    const stats = [];
    const colors = {
      'RTM/GDAM': '#E67E22',
      'GDAM/RTM': '#2ECC71',
      'RTM/DAM': '#3498DB',
      'DAM/GDAM': '#9B59B6'
    };
    const ratio = metrics3;
    if (mcpRatioStats[ratio]) {
      stats.push({
        label: ratio,
        color: colors[ratio],
        mean: mcpRatioStats[ratio].mean,
        stdDev: mcpRatioStats[ratio].stdDev
      });
    }
    return stats;
  }, [metrics3, mcpRatioStats]);

  const isHourlyOr15Min = f.granularity === 'hourly' || f.granularity === '15min' || f.granularity === '15min-raw';
  const axisInterval = f.granularity === '15min' ? 3 : (f.granularity === '15min-raw' ? 2 : 0);
  const axisTickProps = isHourlyOr15Min
    ? { fontSize: 10, angle: 0, textAnchor: 'middle', dy: 10 }
    : { fontSize: 11, angle: -45, textAnchor: 'end', dy: 15 };
  const formatTick = v => {
    if (!v) return '';
    if (f.granularity === '15min-raw') {
      if (typeof v === 'string' && v.includes(' ')) {
        return v.split(' ')[1];
      }
      return v;
    }
    if (f.granularity === 'hourly' || f.granularity === '15min') return v;
    const d = dayjs(v);
    if (!d.isValid()) return v;
    if (f.granularity === 'monthly') return d.format('MMM-YY');
    if (f.granularity === 'yearly') return d.format('YYYY');
    return d.format('DD-MMM');
  };

  const brushPropsData = {};

  return (
    <div id="dashboard-market" style={{ display: 'flex', minHeight: '100vh' }}>
      <div style={{ flex: 1, padding: '12px 24px 24px', overflowX: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <Typography.Title level={4} style={{ margin: 0 }}>Market Data Analysis</Typography.Title>
        </div>

        <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
          {
            key: 'summary',
            label: 'Summary',
            children: <MarketSummaryTab />
          },
          {
            key: 'analysis',
            label: 'Analysis',
            children: (
              <Spin spinning={loading}>
                {(() => {
                  const calculateMetrics = (dataset, segs) => {
                    if (!dataset || dataset.length === 0 || segs.length === 0) return null;
                    let allMcp = [];
                    dataset.forEach(d => {
                      segs.forEach(seg => {
                        const keyMcp = `${seg.toLowerCase()}_mcp`;
                        if (d[keyMcp] != null) allMcp.push({ val: d[keyMcp], period: d.period, seg });
                      });
                    });

                    if (!allMcp.length) return null;

                    const maxMcp = allMcp.reduce((max, obj) => obj.val > max.val ? obj : max, allMcp[0]);
                    const minMcp = allMcp.reduce((min, obj) => obj.val < min.val ? obj : min, allMcp[0]);
                    const avgMcp = allMcp.reduce((sum, obj) => sum + obj.val, 0) / allMcp.length;

                    // Compute average of daily sum of MCV
                    const dailySums = dataset.map(d => {
                      let sumMcv = 0;
                      segs.forEach(seg => {
                        const keyMcv = `${seg.toLowerCase()}_mcv`;
                        if (d[keyMcv] != null) sumMcv += d[keyMcv];
                      });
                      return sumMcv;
                    });
                    const avgMcv = dailySums.reduce((sum, val) => sum + val, 0) / dailySums.length;

                    return { maxMcp, minMcp, avgMcp, avgMcv };
                  };

                  const globalMetrics = calculateMetrics(data, segments);

                  const chartData = data.map(d => {
                    let sumMcp = 0, countMcp = 0;
                    let sumMcv = 0, countMcv = 0;
                    segments.forEach(seg => {
                      const keyMcp = `${seg.toLowerCase()}_mcp`;
                      const keyMcv = `${seg.toLowerCase()}_mcv`;
                      if (d[keyMcp] != null) { sumMcp += d[keyMcp]; countMcp++; }
                      if (d[keyMcv] != null) { sumMcv += d[keyMcv]; countMcv++; }
                    });
                    return {
                      ...d,
                      agg_mcp: countMcp > 0 ? sumMcp / countMcp : null,
                      agg_mcv: countMcv > 0 ? sumMcv : null, // sum of DAM, GDAM, RTM MCV
                    };
                  });

                  const brushProps = {};

                  const mcpStats = {};
                  ['GDAM', 'DAM', 'RTM'].forEach(seg => {
                    const key = `${seg.toLowerCase()}_mcp`;
                    const values = chartData
                      .map(d => d[key])
                      .filter(v => v !== null && v !== undefined && !isNaN(v));

                    if (values.length > 0) {
                      const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
                      const variance = values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / values.length;
                      const stdDev = Math.sqrt(variance);
                      mcpStats[seg] = { mean, stdDev };
                    } else {
                      mcpStats[seg] = { mean: 0, stdDev: 0 };
                    }
                  });

                  const formatStat = (val) => {
                    if (val === null || val === undefined || isNaN(val)) return '-';
                    if (Math.abs(val) > 0 && Math.abs(val) < 0.1) {
                      return `${val.toFixed(4)} ₹/kWh`;
                    }
                    return `${val.toFixed(2)} ₹/kWh`;
                  };

                  const activeStats = [];
                  if (segments.includes('GDAM') && mcpStats['GDAM']) {
                    activeStats.push({
                      label: 'GDAM MCP',
                      color: '#006400',
                      mean: mcpStats['GDAM'].mean,
                      stdDev: mcpStats['GDAM'].stdDev
                    });
                  }
                  if (segments.includes('DAM') && mcpStats['DAM']) {
                    activeStats.push({
                      label: 'DAM MCP',
                      color: '#00008B',
                      mean: mcpStats['DAM'].mean,
                      stdDev: mcpStats['DAM'].stdDev
                    });
                  }
                  if (segments.includes('RTM') && mcpStats['RTM']) {
                    activeStats.push({
                      label: 'RTM MCP',
                      color: '#8B0000',
                      mean: mcpStats['RTM'].mean,
                      stdDev: mcpStats['RTM'].stdDev
                    });
                  }

                  return (
                    <>
                      {/* Chart 1: MCP-MCV Overview */}
                      <Card id="chart-mcp-mcv" title="MCP & MCV Overview" size="small" style={expandedChart === 'chart1' ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { marginBottom: 16 }}
                        extra={
                          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                            <Checkbox.Group
                              options={['GDAM', 'DAM', 'RTM']}
                              value={segments}
                              onChange={setSegments}
                            />
                            <Checkbox checked={showAggregate} onChange={e => setShowAggregate(e.target.checked)}>Aggregate</Checkbox>
                            <Radio.Group value={view1} onChange={e => setView1(e.target.value)} size="small">
                              <Radio.Button value="chart">Chart</Radio.Button>
                              <Radio.Button value="table">Table</Radio.Button>
                            </Radio.Group>
                            {view1 === 'chart' ? (
                              <DownloadButton onPng={() => exportCardAsPng('chart-mcp-mcv', `MarketData_MCP_MCV_${dayjs().format('MMMYYYY')}`)} />
                            ) : (
                              <DownloadButton onCsv={() => exportTableAsCsv(tableData1, columns1, `MarketData_MCP_MCV_${dayjs().format('MMMYYYY')}`)} />
                            )}
                            <Button icon={expandedChart === 'chart1' ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart(expandedChart === 'chart1' ? null : 'chart1')} />
                          </div>
                        }>
                        {view1 === 'chart' ? (
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 24,
                            height: expandedChart === 'chart1' ? 'calc(100vh - 120px)' : 'auto',
                            overflowY: expandedChart === 'chart1' ? 'auto' : 'visible',
                            paddingBottom: expandedChart === 'chart1' ? 24 : 0
                          }}>
                            {/* MCP Chart */}
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: '#1B4F72' }}>Market Clearing Price (MCP)</div>
                              <ResponsiveContainer width="100%" height={300}>
                                <ComposedChart data={chartData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="period" interval={axisInterval} height={70} tick={axisTickProps} tickFormatter={formatTick} />
                                  <YAxis tick={{ fontSize: 11 }} tickFormatter={formatNumberKM} label={{ value: 'Price (₹/kWh)', angle: -90, position: 'insideBottomLeft', offset: 15, style: { fontSize: 11, textAnchor: 'start', dx: -30 } }} />
                                  <Tooltip
                                    itemSorter={tooltipSorter}
                                    labelFormatter={(label) => {
                                      if (f.granularity === '15min-raw') {
                                        const d = dayjs(label);
                                        return d.isValid() ? d.format('DD-MMM-YYYY HH:mm') : label;
                                      }
                                      if (isHourlyOr15Min) return `Time: ${label}${f.granularity === 'hourly' ? ':00' : ''}`;
                                      const d = dayjs(label);
                                      if (f.granularity === 'monthly') return d.isValid() ? d.format('MMM-YY') : label;
                                      if (f.granularity === 'yearly') return d.format('YYYY');
                                      return d.format('DD MMM, YYYY');
                                    }}
                                    formatter={(v, n) => [`₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/kWh`, n]}
                                  />
                                  <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 40 }} />
                                  <Brush dataKey="period" height={20} stroke="#888" {...brushProps} />

                                  {showAggregate ? (
                                    segments.length > 0 && <Line type="monotone" dataKey="agg_mcp" name="Aggregated MCP" stroke="#00008B" strokeWidth={2} dot={false} />
                                  ) : (
                                    <>
                                      {segments.includes('GDAM') && <Line type="monotone" dataKey="gdam_mcp" name="GDAM MCP" stroke="#006400" strokeWidth={2} dot={false} />}
                                      {segments.includes('DAM') && <Line type="monotone" dataKey="dam_mcp" name="DAM MCP" stroke="#00008B" strokeWidth={2} dot={false} />}
                                      {segments.includes('RTM') && <Line type="monotone" dataKey="rtm_mcp" name="RTM MCP" stroke="#8B0000" strokeWidth={2} dot={false} />}
                                    </>
                                  )}
                                </ComposedChart>
                              </ResponsiveContainer>
                            </div>

                            {/* Statistics Summary Section */}
                            {activeStats.length > 0 && (
                              <div style={{
                                marginTop: 16,
                                marginBottom: 16,
                                padding: '16px 24px',
                                borderTop: '1px solid #f0f0f0',
                                borderBottom: '1px solid #f0f0f0',
                                fontFamily: 'Inter, sans-serif'
                              }}>
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-around',
                                  alignItems: 'center',
                                  flexWrap: 'wrap',
                                  gap: 16,
                                  marginBottom: 16
                                }}>
                                  {activeStats.map((item, idx) => (
                                    <React.Fragment key={item.label}>
                                      {idx > 0 && <div style={{ width: 1, height: 50, backgroundColor: '#e2e8f0' }} />}
                                      <div style={{
                                        flex: 1,
                                        textAlign: 'center',
                                        minWidth: '150px'
                                      }}>
                                        <div style={{ color: item.color, fontWeight: 700, fontSize: 13, marginBottom: 8 }}>{item.label}</div>
                                        <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>
                                          Mean: <span style={{ fontWeight: 600, color: '#222' }}>{formatStat(item.mean)}</span>
                                        </div>
                                        <div style={{ fontSize: 12, color: '#555' }}>
                                          Std. Deviation (σ): <span style={{ fontWeight: 600, color: '#222' }}>{formatStat(item.stdDev)} ({item.mean > 0 ? ((item.stdDev / item.mean) * 100).toFixed(2) : '0.00'}%)</span>
                                        </div>
                                      </div>
                                    </React.Fragment>
                                  ))}
                                </div>

                                {/* Callout Box */}
                                {/* <div style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 10,
                            backgroundColor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: 6,
                            padding: '10px 14px',
                            fontSize: 11,
                            color: '#64748b',
                            lineHeight: '1.5'
                          }}>
                            <span style={{ fontSize: 14, color: '#3b82f6', lineHeight: 1 }}>ⓘ</span>
                            <div>
                              Mean represents the average MCP over the selected time period.<br />
                              Std. Deviation (σ) indicates the volatility of MCP around the mean.
                            </div>
                          </div> */}
                              </div>
                            )}

                            {/* MCV Chart */}
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: '#1B4F72' }}>Market Clearing Volume (MCV)</div>
                              <ResponsiveContainer width="100%" height={345}>
                                <ComposedChart data={chartData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="period" interval={axisInterval} height={95} tick={axisTickProps} tickFormatter={formatTick} />
                                  <YAxis tick={{ fontSize: 11 }} tickFormatter={formatNumberKM} label={{ value: 'Volume (MU)', angle: -90, position: 'insideBottomLeft', offset: 15, style: { fontSize: 11, textAnchor: 'start', dx: -30 } }} />
                                  <Tooltip
                                    itemSorter={tooltipSorter}
                                    labelFormatter={(label) => {
                                      if (f.granularity === '15min-raw') {
                                        const d = dayjs(label);
                                        return d.isValid() ? d.format('DD-MMM-YYYY HH:mm') : label;
                                      }
                                      if (isHourlyOr15Min) return `Time: ${label}${f.granularity === 'hourly' ? ':00' : ''}`;
                                      const d = dayjs(label);
                                      if (f.granularity === 'monthly') return d.isValid() ? d.format('MMM-YY') : label;
                                      if (f.granularity === 'yearly') return d.format('YYYY');
                                      return d.format('DD MMM, YYYY');
                                    }}
                                    formatter={(v, n) => [`${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MU`, n]}
                                  />
                                  <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 30 }} />
                                  <Brush dataKey="period" height={20} stroke="#888" {...brushProps} />

                                  {showAggregate ? (
                                    segments.length > 0 && <Area type="monotone" dataKey="agg_mcv" name="Aggregated MCV" stroke="#0052CC" fill="#0052CC" opacity={0.6} />
                                  ) : (
                                    <>
                                      {segments.includes('GDAM') && <Area type="monotone" dataKey="gdam_mcv" name="GDAM MCV" stackId="1" stroke="#27AE60" fill="#27AE60" opacity={0.6} />}
                                      {segments.includes('DAM') && <Area type="monotone" dataKey="dam_mcv" name="DAM MCV" stackId="1" stroke="#0052CC" fill="#0052CC" opacity={0.6} />}
                                      {segments.includes('RTM') && <Area type="monotone" dataKey="rtm_mcv" name="RTM MCV" stackId="1" stroke="#E74C3C" fill="#E74C3C" opacity={0.6} />}
                                    </>
                                  )}
                                </ComposedChart>
                              </ResponsiveContainer>
                            </div>
                          </div>
                        ) : (
                          <Table dataSource={tableData1} columns={columns1} size="small" scroll={{ x: 'max-content', y: 340 }} pagination={false} rowKey="period" />
                        )}
                      </Card>
                    </>
                  );
                })()}

                {/* Chart 2: Avg MCP vs Weighted Avg Realization */}
                {!isCurrentDateSelected && (
                  <Card id="chart-mcp-realization" title="Avg. MCP vs Weighted Avg. Realization" size="small" style={expandedChart === 'chart2' ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { marginBottom: 16 }}
                    extra={
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <Select
                          mode="multiple"
                          placeholder="Select Metrics"
                          value={metrics2}
                          onChange={setMetrics2}
                          style={{ minWidth: 200, maxWidth: 300 }}
                          maxTagCount={1}
                          options={[
                            { value: 'gdam_mcp', label: 'GDAM MCP' },
                            { value: 'dam_mcp', label: 'DAM MCP' },
                            { value: 'rtm_mcp', label: 'RTM MCP' },
                            { value: 'dam_plant_realisation', label: 'DAM Plant Realisation' },
                            { value: 'gdam_plant_realisation', label: 'GDAM Plant Realisation' },
                            { value: 'rtm_plant_realisation', label: 'RTM Plant Realisation' },
                            { value: 'gross_rev_exch', label: 'Wtd Avg MCP' },
                            { value: 'gross_tariff_realized_plant', label: 'Gross Plant Realisation' },
                            { value: 'gross_tariff_realized_exch', label: 'Gross Tariff Exchange' },
                          ]}
                        />
                        <Radio.Group value={view2} onChange={e => setView2(e.target.value)} size="small">
                          <Radio.Button value="chart">Chart</Radio.Button>
                          <Radio.Button value="table">Table</Radio.Button>
                        </Radio.Group>
                        {view2 === 'chart' ? (
                          <DownloadButton onPng={() => exportCardAsPng('chart-mcp-realization', `MarketData_Realization_${dayjs().format('MMMYYYY')}`)} />
                        ) : (
                          <DownloadButton onCsv={() => exportTableAsCsv(tableData2, columns2, `MarketData_Realization_${dayjs().format('MMMYYYY')}`)} />
                        )}
                        <Button icon={expandedChart === 'chart2' ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart(expandedChart === 'chart2' ? null : 'chart2')} />
                      </div>
                    }>
                    {view2 === 'chart' ? (
                      <div style={{ height: expandedChart === 'chart2' ? 'calc(100vh - 120px)' : 360 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart data={data} syncId="chart2">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="period" interval={axisInterval} height={70} tick={axisTickProps} tickFormatter={formatTick} />
                            <YAxis tick={{ fontSize: 11 }} tickFormatter={formatNumberKM} label={{ value: 'Price (₹/kWh)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                            <Tooltip
                              itemSorter={tooltipSorter}
                              labelFormatter={(label) => {
                                if (f.granularity === '15min-raw') {
                                  const d = dayjs(label);
                                  return d.isValid() ? d.format('DD-MMM-YYYY HH:mm') : label;
                                }
                                if (isHourlyOr15Min) return `Time: ${label}${f.granularity === 'hourly' ? ':00' : ''}`;
                                const d = dayjs(label);
                                if (f.granularity === 'monthly') return d.isValid() ? d.format('MMM-YY') : label;
                                if (f.granularity === 'yearly') return d.format('YYYY');
                                return d.format('DD MMM, YYYY');
                              }}
                              formatter={(v, n) => [`₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/kWh`, n]}
                            />
                            <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 30 }} />
                            <Brush dataKey="period" height={20} stroke="#888" {...brushPropsData} />

                            {metrics2.includes('gdam_mcp') && <Line type="monotone" dataKey="gdam_mcp" name="GDAM MCP" stroke="#27AE60" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />}
                            {metrics2.includes('dam_mcp') && <Line type="monotone" dataKey="dam_mcp" name="DAM MCP" stroke="#0052CC" strokeWidth={2} dot={false} />}
                            {metrics2.includes('rtm_mcp') && <Line type="monotone" dataKey="rtm_mcp" name="RTM MCP" stroke="#E74C3C" strokeWidth={2} dot={false} />}
                            {metrics2.includes('dam_plant_realisation') && <Line type="monotone" dataKey="dam_plant_realisation" name="DAM Plant Realisation" stroke="#00008B" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />}
                            {metrics2.includes('gdam_plant_realisation') && <Line type="monotone" dataKey="gdam_plant_realisation" name="GDAM Plant Realisation" stroke="#006400" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />}
                            {metrics2.includes('rtm_plant_realisation') && <Line type="monotone" dataKey="rtm_plant_realisation" name="RTM Plant Realisation" stroke="#8B0000" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />}
                            {metrics2.includes('gross_rev_exch') && <Line type="monotone" dataKey="gross_rev_exch" name="Wtd Avg MCP" stroke="#8E44AD" strokeWidth={2} dot={false} />}
                            {metrics2.includes('gross_tariff_realized_plant') && <Line type="monotone" dataKey="gross_tariff_realized_plant" name="Gross Plant Realisation" stroke="#16A085" strokeWidth={2} dot={false} />}
                            {metrics2.includes('gross_tariff_realized_exch') && <Line type="monotone" dataKey="gross_tariff_realized_exch" name="Gross Tariff Exchange" stroke="#2C3E50" strokeWidth={2} dot={false} />}
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <Table dataSource={tableData2} columns={columns2} size="small" scroll={{ x: 'max-content', y: 300 }} pagination={false} rowKey="period" />
                    )}
                  </Card>
                )}

                {/* Chart 3: MCP Ratios */}
                {!isCurrentDateSelected && (
                  <Card id="chart-mcp-ratios"
                    title="MCP Ratios Trend"
                    size="small" style={expandedChart === 'chart3' ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { marginBottom: 16 }}
                    extra={
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <Select size="small" value={metrics3} onChange={setMetrics3} style={{ width: 160 }}
                          options={[
                            { label: 'RTM/GDAM', value: 'RTM/GDAM' },
                            // { label: 'GDAM/RTM', value: 'GDAM/RTM' },
                            { label: 'RTM/DAM', value: 'RTM/DAM' },
                            { label: 'DAM/GDAM', value: 'DAM/GDAM' },
                          ]} />
                        <Radio.Group value={view3} onChange={e => setView3(e.target.value)} size="small">
                          <Radio.Button value="chart">Chart</Radio.Button>
                          <Radio.Button value="table">Table</Radio.Button>
                        </Radio.Group>
                        {view3 === 'chart' ? (
                          <DownloadButton onPng={() => exportCardAsPng('chart-mcp-ratios', `MCP_Ratios_${dayjs().format('MMMYYYY')}`)} />
                        ) : (
                          <DownloadButton onCsv={() => exportTableAsCsv(tableData3, columns3, `MCP_Ratios_${dayjs().format('MMMYYYY')}`)} />
                        )}
                        <Button icon={expandedChart === 'chart3' ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart(expandedChart === 'chart3' ? null : 'chart3')} />
                      </div>
                    }>
                    {view3 === 'chart' ? (
                      <>
                        <div style={{ height: expandedChart === 'chart3' ? 'calc(100vh - 120px)' : 360 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={chart3Data} margin={{ left: 20, right: 10, top: 10, bottom: 20 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="period" interval={axisInterval} height={80} tick={axisTickProps} tickFormatter={formatTick} />
                              <YAxis tick={{ fontSize: 11 }} width={50} tickFormatter={v => Number(v).toFixed(2)} domain={[0, dataMax => (isNaN(dataMax) || dataMax == null) ? 1.1 : Math.max(dataMax, 1.1)]} label={{ value: 'Ratio', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                              <ReferenceLine y={1} stroke="#64748b" strokeDasharray="3 3" label={{ value: 'Ratio = 1', position: 'insideBottomRight', fill: '#475569', fontSize: 10, fontWeight: '600' }} />
                              <Tooltip itemSorter={tooltipSorter} shared={false} labelFormatter={(label) => {
                                if (f.granularity === '15min-raw') {
                                  const d = dayjs(label);
                                  return d.isValid() ? d.format('DD-MMM-YYYY HH:mm') : label;
                                }
                                if (isHourlyOr15Min) return `Time: ${label}${f.granularity === 'hourly' ? ':00' : ''}`;
                                const d = dayjs(label);
                                if (f.granularity === 'monthly') return d.isValid() ? d.format('MMM-YY') : label;
                                if (f.granularity === 'yearly') return d.format('YYYY');
                                return d.format('DD MMM, YYYY');
                              }} formatter={(v, n) => [Number(v).toFixed(3), n]} />
                              <Legend wrapperStyle={{ width: '100%', left: 30 }} content={(props) => (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                                  {renderCustomLegend(props)}
                                  {activeRatioStats[0] && (
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      gap: 16,
                                      fontSize: 11,
                                      color: '#555',
                                      fontWeight: 500,
                                      marginTop: 2
                                    }}>
                                      <span>Mean: <strong style={{ color: activeRatioStats[0].color }}>{formatRatioStat(activeRatioStats[0].mean)}</strong></span>
                                      <span style={{ color: '#cbd5e1' }}>|</span>
                                      <span>Std. Deviation (σ): <strong style={{ color: activeRatioStats[0].color }}>{formatRatioStat(activeRatioStats[0].stdDev)} ({activeRatioStats[0].mean > 0 ? ((activeRatioStats[0].stdDev / activeRatioStats[0].mean) * 100).toFixed(2) : '0.00'}%)</strong></span>
                                    </div>
                                  )}
                                </div>
                              )} />
                              <Brush dataKey="period" height={20} stroke="#888" {...brushPropsData} />
                              {metrics3 === 'RTM/GDAM' && <Line type="monotone" dataKey="RTM/GDAM" name="RTM/GDAM" stroke="#E67E22" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />}
                              {/* {metrics3 === 'GDAM/RTM' && <Line type="monotone" dataKey="GDAM/RTM" name="GDAM/RTM" stroke="#2ECC71" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />} */}
                              {metrics3 === 'RTM/DAM' && <Line type="monotone" dataKey="RTM/DAM" name="RTM/DAM" stroke="#3498DB" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />}
                              {metrics3 === 'DAM/GDAM' && <Line type="monotone" dataKey="DAM/GDAM" name="DAM/GDAM" stroke="#9B59B6" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />}
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </>
                    ) : (
                      <Table dataSource={tableData3} columns={columns3} size="small" scroll={{ x: 'max-content', y: 280 }} pagination={false} rowKey="period" />
                    )}
                  </Card>
                )}

                {/* Buy / Sell Bid Ratio */}
                <Card
                  id="chart-buy-sell-bid-ratio"
                  title="Buy / Sell Bid Ratio"
                  size="small"
                  style={expandedChart === 'chart_bid_ratio' ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { marginBottom: 16 }}
                  extra={
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <Checkbox.Group
                        options={['GDAM', 'DAM', 'RTM']}
                        value={segmentsBidRatio}
                        onChange={setSegmentsBidRatio}
                      />
                      <Radio.Group value={viewBidRatio} onChange={e => setViewBidRatio(e.target.value)} size="small">
                        <Radio.Button value="chart">Chart</Radio.Button>
                        <Radio.Button value="table">Table</Radio.Button>
                      </Radio.Group>
                      {viewBidRatio === 'chart' ? (
                        <DownloadButton onPng={() => exportCardAsPng('chart-buy-sell-bid-ratio', `Buy_Sell_Bid_Ratio_${dayjs().format('MMMYYYY')}`)} />
                      ) : (
                        <DownloadButton onCsv={() => exportTableAsCsv(tableDataBidRatio, columnsBidRatio, `Buy_Sell_Bid_Ratio_${dayjs().format('MMMYYYY')}`)} />
                      )}
                      <Button icon={expandedChart === 'chart_bid_ratio' ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart(expandedChart === 'chart_bid_ratio' ? null : 'chart_bid_ratio')} />
                    </div>
                  }
                >
                  {viewBidRatio === 'chart' ? (
                    <div style={{ height: expandedChart === 'chart_bid_ratio' ? 'calc(100vh - 120px)' : 360 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartBidRatioData} margin={{ left: 20, right: 10, top: 10, bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="period" interval={axisInterval} height={80} tick={axisTickProps} tickFormatter={formatTick} />
                          <YAxis
                            tick={{ fontSize: 11 }}
                            width={50}
                            tickFormatter={v => Math.round(Number(v))}
                            domain={['auto', 'auto']}
                            label={{ value: 'Purchase / Sell Ratio', angle: -90, position: 'insideLeft', dy: 40, style: { fontSize: 11 } }}
                          />
                          <Tooltip
                            itemSorter={tooltipSorter}
                            shared={false}
                            labelFormatter={(label) => {
                              if (f.granularity === '15min-raw') {
                                const d = dayjs(label);
                                return d.isValid() ? d.format('DD-MMM-YYYY HH:mm') : label;
                              }
                              if (isHourlyOr15Min) return `Time: ${label}${f.granularity === 'hourly' ? ':00' : ''}`;
                              const d = dayjs(label);
                              if (f.granularity === 'monthly') return d.isValid() ? d.format('MMM-YY') : label;
                              if (f.granularity === 'yearly') return d.format('YYYY');
                              return d.format('DD MMM, YYYY');
                            }}
                            formatter={(val, name) => [Number(val).toFixed(2), name]}
                          />
                          <Legend wrapperStyle={{ width: '100%', left: 30 }} content={renderCustomLegend} />
                          <Brush dataKey="period" height={20} stroke="#888" {...brushPropsData} />
                          {segmentsBidRatio.includes('DAM') && (
                            <Line
                              type="monotone"
                              dataKey="DAM"
                              name="DAM (Purchase/Sell)"
                              stroke="#00008B"
                              strokeWidth={2}
                              dot={false}
                              activeDot={{ r: 6 }}
                            />
                          )}
                          {segmentsBidRatio.includes('GDAM') && (
                            <Line
                              type="monotone"
                              dataKey="GDAM"
                              name="GDAM (Purchase/Sell)"
                              stroke="#006400"
                              strokeWidth={2}
                              dot={false}
                              activeDot={{ r: 6 }}
                            />
                          )}
                          {segmentsBidRatio.includes('RTM') && (
                            <Line
                              type="monotone"
                              dataKey="RTM"
                              name="RTM (Purchase/Sell)"
                              stroke="#8B0000"
                              strokeWidth={2}
                              dot={false}
                              activeDot={{ r: 6 }}
                            />
                          )}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <Table dataSource={tableDataBidRatio} columns={columnsBidRatio} size="small" scroll={{ x: 'max-content', y: 280 }} pagination={false} rowKey="period" />
                  )}
                </Card>

              </Spin>
            )
          }
        ]} />
      </div>

      {/* Right Section: Filters Panel */}
      {activeTab === 'analysis' && <GlobalFilterBar onApply={fetchData} isMarket={true} />}
    </div>
  );
}
